import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Save, Building2, Mail, MessageSquare, CheckCircle, XCircle, Send, FileText, Plus, Trash2, MapPin, Landmark, Users, Palette, Droplet, Layers, Recycle, Trash, Globe, Calendar, AlertCircle, Folder, GitBranch, X, Eye, Workflow } from 'lucide-react';
import DataLinkageMap from '@/components/DataLinkageMap';
import Combobox from '@/components/Combobox';
import ShortenNamesTool from '@/components/ShortenNamesTool';
import BrandNameFix from '@/components/BrandNameFix';
import SaveTemplatesEditor from '@/components/SaveTemplatesEditor';
import FlashingProfileSVG from '@/components/FlashingProfileSVG';
import { formatNzPhone, formatGstNumber } from '@/lib/formatters';

// Common NZ banks for the dropdown. `allowCustom` keeps it open-ended.
const NZ_BANKS = [
  'ANZ',
  'ASB',
  'BNZ',
  'Westpac',
  'Kiwibank',
  'TSB',
  'The Co-operative Bank',
  'Heartland Bank',
  'SBS Bank',
  'HSBC',
  'Rabobank',
  'ICBC',
  'China Construction Bank',
];

export default function CompanySettingsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  // Read the URL once on mount so "Jump to fix" deep-links from the
  // Data Linkage audit can pre-select a tab and pass a filter through
  // to the editor. Subsequent tab clicks update window state via
  // setTab, not the URL — keeps the URL "clean" once the operator has
  // moved past the deep-link.
  const initialQuery = (() => {
    try { return Object.fromEntries(new URLSearchParams(window.location.search)); }
    catch { return {}; }
  })();
  const [tab, setTab] = useState(initialQuery.tab || 'company');
  const [tabFilter] = useState(initialQuery.filter || '');
  const [form, setForm] = useState({});
  const [emailForm, setEmailForm] = useState({});
  const [smsForm, setSmsForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [dropdowns, setDropdowns] = useState({ address_cities: [], address_suburbs: [] });

  const load = useCallback(async () => {
    try {
      const [co, em, sm, dd] = await Promise.all([
        api.get('/company-settings'),
        api.get('/email/settings').catch(() => ({ data: {} })),
        api.get('/sms/settings').catch(() => ({ data: {} })),
        api.get('/dropdowns').catch(() => ({ data: {} })),
      ]);
      setForm(co.data || {});
      setEmailForm(em.data || {});
      setSmsForm(sm.data || {});
      setDropdowns({
        address_cities: dd.data?.address_cities || [],
        address_suburbs: dd.data?.address_suburbs || [],
      });
    } catch (e) { console.error(e); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const saveCompany = async (e) => {
    e.preventDefault(); setSaving(true); setSaved(false);
    const { _id, ...payload } = form;
    await api.put('/company-settings', payload);
    setSaved(true); setTimeout(() => setSaved(false), 2000); setSaving(false);
  };

  const saveEmail = async (e) => {
    e.preventDefault(); setSaving(true); setSaved(false);
    const { _id, configured, ...payload } = emailForm;
    await api.put('/email/settings', payload);
    setSaved(true); setTimeout(() => setSaved(false), 2000); setSaving(false); load();
  };

  const saveSms = async (e) => {
    e.preventDefault(); setSaving(true); setSaved(false);
    const { _id, configured, ...payload } = smsForm;
    await api.put('/sms/settings', payload);
    setSaved(true); setTimeout(() => setSaved(false), 2000); setSaving(false); load();
  };

  const [testingEmail, setTestingEmail] = useState(false);
  const testEmail = async () => {
    if (testingEmail) return;
    setTestResult(null); setTestingEmail(true);
    try {
      const { data } = await api.post('/email/send', { to_email: emailForm.from_email || emailForm.smtp_user, subject: 'RTR Test Email', body: "This is a test email from Roofs To Remedy Ltd CRM." });
      setTestResult(data);
    } catch (e) {
      setTestResult({ status: 'failed', message: e.response?.data?.detail || e.message });
    } finally { setTestingEmail(false); }
  };

  const testSms = async () => {
    setTestResult(null);
    const to = window.prompt(
      'Send a test SMS to which number?\n\nEnter in E.164 format e.g. +6421616739 (NZ mobile).',
      '+6421616739'
    );
    if (!to || !to.trim()) return;
    try {
      const { data } = await api.post('/sms/test', { to_number: to.trim() });
      setTestResult(data);
    } catch (e) {
      setTestResult({ status: 'failed', message: e?.response?.data?.detail || e.message });
    }
  };

  // Bulk-normalise every imported phone number across clients, contractors,
  // suppliers, insurance companies/assessors, agents, leads. Idempotent.
  const [normalising, setNormalising] = useState(false);
  const [normaliseResult, setNormaliseResult] = useState(null);
  const normalisePhones = async () => {
    if (!window.confirm('Re-format every phone number across Clients, Contractors, Suppliers, Insurance Companies, Insurance Assessors, Agents and Leads to the (07) 855 1234 / (027) 475 9174 NZ format?\n\nNumbers that are already formatted or are international are left untouched. This is safe to run more than once.')) return;
    setNormalising(true);
    setNormaliseResult(null);
    try {
      const { data } = await api.post('/admin/normalize-phones');
      setNormaliseResult(data);
    } catch (e) {
      setNormaliseResult({ ok: false, error: e?.response?.data?.detail || e.message });
    }
    setNormalising(false);
  };

  // Single-line fields (everything that is NOT a structured group). The
  // optional `format` function is applied on blur so the user can type
  // naturally and the value is normalised when they tab out.
  const companyFields = [
    { k: 'company_name', l: 'Company Name' }, { k: 'tagline', l: 'Tagline' },
    { k: 'phone', l: 'Phone', p: '(07) 855 1234', format: formatNzPhone },
    { k: 'email', l: 'Email' }, { k: 'website', l: 'Website' },
    { k: 'gst_number', l: 'GST Number', p: '123-456-789', format: formatGstNumber },
    { k: 'company_number', l: 'Companies Office Number', p: 'e.g. 1234567' },
    { k: 'nzbn', l: 'NZBN', p: '9429000000000' },
  ];

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const tabs = [
    { key: 'company', label: 'Company', icon: Building2 },
    { key: 'email', label: 'Email (SMTP)', icon: Mail },
    { key: 'sms', label: 'SMS (Twilio)', icon: MessageSquare },
    { key: 'quote-types', label: 'Quote Types', icon: FileText },
    { key: 'pipe-usages', label: 'Pipe Usages', icon: Droplet },
    { key: 'flashing-categories', label: 'Flashing Categories', icon: Layers },
    { key: 'stripping-rates', label: 'Stripping Rates', icon: Recycle },
    { key: 'skip-bin-suppliers', label: 'Skip Bin Suppliers', icon: Trash },
    { key: 'cultural-phrases', label: 'Cultural Phrases', icon: Globe },
    { key: 'cultural-celebrations', label: 'Cultural Celebrations', icon: Calendar },
    { key: 'defect-catalogue', label: 'Defect Catalogue', icon: AlertCircle },
    { key: 'paint-systems', label: 'Paint Systems', icon: Palette },
    { key: 'shorten-names', label: 'Shorten Names', icon: FileText },
    { key: 'save-templates', label: 'Save Templates', icon: Folder },
    { key: 'data-linkage', label: 'Data Linkage', icon: GitBranch },
    // iter301a — Shortcut to the standalone Status Workflows admin
    // page. Uses `href` sentinel that the tab-click handler detects
    // to navigate() instead of switching the inline tab. Full page
    // lives at /settings/status-workflows.
    { key: 'status-workflows', label: 'Status Workflows', icon: Workflow, href: '/settings/status-workflows' },
  ];

  return (
    <div data-testid="company-settings-page" className="space-y-4">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Settings</h1>
        <p className="text-sm text-slate-500 mt-1">Company branding, email & SMS configuration</p>
      </div>
      <div className="flex flex-wrap gap-0.5 border-b border-[#E2E2DF]">
        {tabs.map(t => (
          <button
            key={t.key}
            data-testid={`settings-tab-${t.key}`}
            onClick={() => {
              // iter301a — tabs with an `href` are shortcuts that
              // navigate to a standalone page instead of switching
              // the inline tab.
              if (t.href) { navigate(t.href); return; }
              setTab(t.key); setTestResult(null);
            }}
            className={`flex items-center gap-1.5 px-2.5 py-2 text-xs font-medium border-b-2 whitespace-nowrap transition-all ${tab === t.key ? 'border-[#D35400] text-[#D35400]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          ><t.icon size={13} />{t.label}</button>
        ))}
      </div>

      {tab === 'shorten-names' && (
        <div data-testid="shorten-names-tab" className="space-y-2">
          <ShortenNamesTool />
        </div>
      )}

      {/* Quick links — surface related setup pages so users don't have to hunt for them */}
      <div data-testid="settings-quick-links" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <button
          data-testid="quick-link-pdf-styling"
          onClick={() => navigate('/report-setup?tab=pdf_styling')}
          className="group flex items-center gap-3 p-4 bg-white border border-[#E2E2DF] rounded-md hover:border-[#D35400] hover:shadow-sm transition-all text-left"
        >
          <div className="w-10 h-10 rounded-md bg-[#D35400]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#D35400]/15 transition-colors">
            <Palette size={18} className="text-[#D35400]" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-800">PDF Styling</div>
            <div className="text-[11px] text-slate-500 truncate">Logos, fonts, colors & letter copy</div>
          </div>
        </button>
        <button
          data-testid="quick-link-report-setup"
          onClick={() => navigate('/report-setup')}
          className="group flex items-center gap-3 p-4 bg-white border border-[#E2E2DF] rounded-md hover:border-[#D35400] hover:shadow-sm transition-all text-left"
        >
          <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0 group-hover:bg-slate-200 transition-colors">
            <FileText size={18} className="text-slate-600" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-800">Report Setup</div>
            <div className="text-[11px] text-slate-500 truncate">Templates, GST, invoice & PDF text</div>
          </div>
        </button>
        <button
          data-testid="quick-link-duplicate-clients"
          onClick={() => navigate('/duplicate-clients')}
          className="group flex items-center gap-3 p-4 bg-white border border-[#E2E2DF] rounded-md hover:border-[#1E40AF] hover:shadow-sm transition-all text-left"
        >
          <div className="w-10 h-10 rounded-md bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition-colors">
            <Users size={18} className="text-[#1E40AF]" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-800">Duplicate Clients</div>
            <div className="text-[11px] text-slate-500 truncate">Find & merge duplicate records</div>
          </div>
        </button>
        <button
          data-testid="quick-link-pdf-filename-templates"
          onClick={() => navigate('/admin/pdf-filename-templates')}
          className="group flex items-center gap-3 p-4 bg-white border border-[#E2E2DF] rounded-md hover:border-[#D35400] hover:shadow-sm transition-all text-left"
        >
          <div className="w-10 h-10 rounded-md bg-[#D35400]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#D35400]/15 transition-colors">
            <FileText size={18} className="text-[#D35400]" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-800">PDF Filename Templates</div>
            <div className="text-[11px] text-slate-500 truncate">Tweak Access-style download names</div>
          </div>
        </button>
      </div>

      {/* Company Tab */}
      {tab === 'company' && (
        <div className="space-y-4">
        <BrandNameFix api={api} />
        <form onSubmit={saveCompany} className="bg-white border border-[#E2E2DF] rounded-md p-6 space-y-5">
          <div className="flex items-center gap-3 mb-2"><Building2 size={20} className="text-[#D35400]" /><h3 className="text-lg font-semibold text-slate-800 font-[Manrope]">Company Information</h3></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {companyFields.map(f => (
              <div key={f.k}>
                <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">{f.l}</label>
                <input
                  data-testid={`settings-${f.k}`}
                  value={form[f.k] || ''}
                  onChange={e => setField(f.k, e.target.value)}
                  onBlur={f.format ? () => setField(f.k, f.format(form[f.k] || '')) : undefined}
                  placeholder={f.p || ''}
                  className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400]"
                />
              </div>
            ))}
          </div>

          {/* Company Address (structured) */}
          <div className="pt-4 border-t border-[#E2E2DF]">
            <div className="flex items-center gap-2 mb-3">
              <MapPin size={16} className="text-[#D35400]" />
              <h4 className="text-sm font-semibold text-slate-800 font-[Manrope]">Company Address</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Street</label>
                <input data-testid="settings-address-street" value={form.address_street || ''} onChange={e => setField('address_street', e.target.value)} placeholder="123 Main Street" className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400]" />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Suburb</label>
                <Combobox
                  testId="settings-address-suburb"
                  value={form.address_suburb || ''}
                  onChange={v => setField('address_suburb', v)}
                  options={dropdowns.address_suburbs}
                  placeholder="Start typing a suburb…"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">City</label>
                <Combobox
                  testId="settings-address-city"
                  value={form.address_city || ''}
                  onChange={v => setField('address_city', v)}
                  options={dropdowns.address_cities}
                  placeholder="Hamilton"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Postcode</label>
                <input data-testid="settings-address-postcode" value={form.address_postcode || ''} onChange={e => setField('address_postcode', e.target.value)} placeholder="3204" className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400]" />
              </div>
            </div>
            {(form.address || form.address_street) && (
              <p className="mt-2 text-[11px] text-slate-400">
                <span className="font-bold uppercase tracking-[0.15em]">Preview on PDFs:</span>{' '}
                <span className="font-mono">{[form.address_street, form.address_suburb, form.address_city].filter(Boolean).join(', ')}{form.address_postcode ? ' ' + form.address_postcode : ''}</span>
                {!form.address_street && form.address && <span className="italic ml-1">(falling back to legacy single-line address)</span>}
              </p>
            )}
          </div>

          {/* Bank Account (structured) */}
          <div className="pt-4 border-t border-[#E2E2DF]">
            <div className="flex items-center gap-2 mb-3">
              <Landmark size={16} className="text-[#D35400]" />
              <h4 className="text-sm font-semibold text-slate-800 font-[Manrope]">Bank Account</h4>
              <span className="text-[11px] text-slate-400 italic">Printed on Quotation PDFs under "Payment Terms"</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Bank</label>
                <Combobox
                  testId="settings-bank-name"
                  value={form.bank_name || ''}
                  onChange={v => setField('bank_name', v)}
                  options={NZ_BANKS}
                  placeholder="Westpac"
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Account Name</label>
                <input data-testid="settings-bank-account-holder" value={form.bank_account_holder || ''} onChange={e => setField('bank_account_holder', e.target.value)} placeholder="Roofs To Remedy Ltd" className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400]" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Account Number</label>
                <input data-testid="settings-bank-account-number" value={form.bank_account_number || ''} onChange={e => setField('bank_account_number', e.target.value)} placeholder="03 - 0314 - 0349045 - 001" className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400] font-mono-num" />
              </div>
            </div>
            {(form.bank_name || form.bank_account_number || form.bank_account_holder) && (
              <p className="mt-2 text-[11px] text-slate-400">
                <span className="font-bold uppercase tracking-[0.15em]">Preview on PDFs:</span>{' '}
                <span className="font-mono">{[form.bank_name, form.bank_account_number, form.bank_account_holder].filter(Boolean).join('  ')}</span>
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">Terms & Conditions (shown on PDFs)</label>
            <textarea data-testid="settings-terms" value={form.terms_and_conditions || ''} onChange={e => setField('terms_and_conditions', e.target.value)} rows={3} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D35400]" />
          </div>
          <div className="flex justify-end">
            <button data-testid="save-company-settings" type="submit" disabled={saving} className={`flex items-center gap-2 px-6 py-2.5 rounded-md text-sm font-semibold transition-all ${saved ? 'bg-green-500 text-white' : 'bg-[#D35400] hover:bg-[#A04000] text-white'} disabled:opacity-50`}><Save size={14} /> {saving ? 'Saving...' : saved ? 'Saved!' : 'Save'}</button>
          </div>
        </form>

        {/* Data Maintenance — bulk normalise phone numbers across the database */}
        <div data-testid="data-maintenance-section" className="bg-white border border-[#E2E2DF] rounded-md p-6 space-y-3">
          <div className="flex items-center gap-3 mb-2">
            <MessageSquare size={20} className="text-[#1E40AF]" />
            <h3 className="text-lg font-semibold text-slate-800 font-[Manrope]">Data Maintenance</h3>
          </div>
          <div className="flex items-start gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800">Normalise phone numbers</p>
              <p className="text-[12px] text-slate-500 mt-0.5">
                Re-format every imported phone field across Clients, Contractors, Suppliers, Insurance Companies, Assessors, Agents and Leads to the standard
                <span className="font-mono mx-1">(07) 855 1234</span> /
                <span className="font-mono ml-1">(027) 475 9174</span> NZ form.
                Numbers already formatted or international are left untouched. Safe to run more than once.
              </p>
            </div>
            <button
              data-testid="normalise-phones-btn"
              onClick={normalisePhones}
              disabled={normalising}
              className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-md text-sm font-semibold transition-all disabled:opacity-60"
            >
              {normalising ? 'Normalising…' : 'Normalise All Phone Numbers'}
            </button>
          </div>
          {normaliseResult && (
            <div data-testid="normalise-phones-result" className={`mt-3 rounded-md p-3 text-xs ${normaliseResult.ok ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-rose-50 border border-rose-200 text-rose-700'}`}>
              {normaliseResult.ok ? (
                <>
                  <strong>Done.</strong>{' '}
                  Updated <span className="font-mono font-semibold">{normaliseResult.totals?.fields_updated || 0}</span> phone field
                  {(normaliseResult.totals?.fields_updated || 0) === 1 ? '' : 's'} across{' '}
                  <span className="font-mono font-semibold">{normaliseResult.totals?.documents_modified || 0}</span> record
                  {(normaliseResult.totals?.documents_modified || 0) === 1 ? '' : 's'}.
                  <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
                    {Object.entries(normaliseResult.summary || {}).map(([coll, s]) => (
                      <div key={coll} className="bg-white/60 rounded px-2 py-1 border border-emerald-100">
                        <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500">{coll.replace('_', ' ')}</div>
                        <div className="font-mono text-[11px] text-slate-700">{s.documents_modified}/{s.scanned} records · {s.fields_updated} fields</div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <><strong>Failed.</strong> {normaliseResult.error || 'Unknown error.'}</>
              )}
            </div>
          )}

          {/* Duplicate Clients Finder shortcut */}
          <div className="border-t border-[#E2E2DF] pt-3 mt-3 flex items-start gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800">Find duplicate clients</p>
              <p className="text-[12px] text-slate-500 mt-0.5">
                Surface clients sharing the same email, surname + phone, or company name. Pick a keeper and merge — every property, appraisal, lead and claim moves to the keeper before the duplicate is deleted.
              </p>
            </div>
            <button
              data-testid="open-duplicate-clients"
              onClick={() => navigate('/duplicate-clients')}
              className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-md text-sm font-semibold transition-all"
            >
              <Users size={14} /> Open Duplicate Clients Finder
            </button>
          </div>
        </div>

        {/* Take Ownership / Emergent Instructions */}
        <div data-testid="emergent-ownership-section" className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl p-6 text-white space-y-5">
          <div>
            <h3 className="text-lg font-bold font-[Manrope]">Take Ownership of This CRM</h3>
            <p className="text-sm text-slate-300 mt-1">This CRM was built on the Emergent AI platform. Follow the steps below to take full control, make changes, and host it under your own account.</p>
          </div>

          <div className="space-y-4">
            <div className="bg-white/10 rounded-lg p-4 space-y-2">
              <h4 className="text-sm font-bold text-[#D35400]">Step 1: Create Your Emergent Account</h4>
              <p className="text-xs text-slate-300 leading-relaxed">Go to <a href="https://emergent.sh" target="_blank" rel="noopener noreferrer" className="text-[#D35400] underline hover:text-orange-300 font-semibold">emergent.sh</a> and sign up for an account. You can start with the Free plan to explore, then upgrade when ready.</p>
            </div>

            <div className="bg-white/10 rounded-lg p-4 space-y-3">
              <h4 className="text-sm font-bold text-[#D35400]">Step 2: Choose a Plan</h4>
              <p className="text-xs text-slate-300 mb-2">Emergent uses credits to power AI tasks. Here's what each plan offers:</p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-slate-400 border-b border-white/10">
                      <th className="py-1.5 pr-3">Plan</th>
                      <th className="py-1.5 pr-3">Monthly</th>
                      <th className="py-1.5 pr-3">Credits</th>
                      <th className="py-1.5">Best For</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-300">
                    <tr className="border-b border-white/5"><td className="py-1.5 pr-3 font-semibold">Free</td><td className="py-1.5 pr-3">$0</td><td className="py-1.5 pr-3">10</td><td className="py-1.5">Exploring & small tweaks</td></tr>
                    <tr className="border-b border-white/5 bg-white/5"><td className="py-1.5 pr-3 font-semibold text-[#D35400]">Standard</td><td className="py-1.5 pr-3">$20 USD</td><td className="py-1.5 pr-3">100</td><td className="py-1.5">Regular updates & hosting</td></tr>
                    <tr className="border-b border-white/5"><td className="py-1.5 pr-3 font-semibold">Pro</td><td className="py-1.5 pr-3">$200 USD</td><td className="py-1.5 pr-3">750</td><td className="py-1.5">Heavy development & new features</td></tr>
                    <tr><td className="py-1.5 pr-3 font-semibold">Top-ups</td><td className="py-1.5 pr-3">$8 USD</td><td className="py-1.5 pr-3">50</td><td className="py-1.5">Never expire, buy as needed</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white/10 rounded-lg p-4 space-y-2">
              <h4 className="text-sm font-bold text-[#D35400]">Step 3: Estimated Ongoing Credit Cost</h4>
              <p className="text-xs text-slate-300 leading-relaxed">For day-to-day CRM operation (hosting, small changes, bug fixes):</p>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div className="bg-white/5 rounded-md px-3 py-2">
                  <div className="text-[10px] text-slate-400 uppercase">Hosting Only</div>
                  <div className="text-sm font-bold text-white">~$20 USD/mo</div>
                  <div className="text-[10px] text-slate-400">Standard plan, no changes</div>
                </div>
                <div className="bg-white/5 rounded-md px-3 py-2">
                  <div className="text-[10px] text-slate-400 uppercase">Monthly Tweaks</div>
                  <div className="text-sm font-bold text-white">~$20-40 USD/mo</div>
                  <div className="text-[10px] text-slate-400">Standard + occasional top-ups</div>
                </div>
                <div className="bg-white/5 rounded-md px-3 py-2">
                  <div className="text-[10px] text-slate-400 uppercase">Regular Development</div>
                  <div className="text-sm font-bold text-white">~$200 USD/mo</div>
                  <div className="text-[10px] text-slate-400">Pro plan for frequent changes</div>
                </div>
                <div className="bg-white/5 rounded-md px-3 py-2">
                  <div className="text-[10px] text-slate-400 uppercase">NZD Equivalent</div>
                  <div className="text-sm font-bold text-white">~$34-340 NZD/mo</div>
                  <div className="text-[10px] text-slate-400">At current exchange rates</div>
                </div>
              </div>
              <p className="text-[10px] text-slate-400 mt-2 italic">Compare: A developer would cost $70-100 NZD/hour. One hour of dev time = 2-5 months of AI hosting.</p>
            </div>

            <div className="bg-white/10 rounded-lg p-4 space-y-2">
              <h4 className="text-sm font-bold text-[#D35400]">Step 4: Transfer This CRM to Your Account</h4>
              <ol className="text-xs text-slate-300 leading-relaxed space-y-1.5 list-decimal ml-4">
                <li>Log into your Emergent account at <a href="https://emergent.sh" target="_blank" rel="noopener noreferrer" className="text-[#D35400] underline">emergent.sh</a></li>
                <li>Ask the current owner to share the project with you, or use <strong>"Save to GitHub"</strong> to push the code to a repo you both have access to</li>
                <li>In your Emergent dashboard, select <strong>"Import from GitHub"</strong> to pull the CRM into your account</li>
                <li>Once imported, you have <strong>full ownership</strong> — deploy, modify, add features, all under your control</li>
                <li>To make changes, simply chat with the AI: describe what you want in plain English and it builds it</li>
              </ol>
            </div>

            <div className="bg-[#D35400]/20 border border-[#D35400]/40 rounded-lg p-4">
              <h4 className="text-sm font-bold text-[#D35400] mb-1">What You Get</h4>
              <ul className="text-xs text-slate-300 space-y-1">
                <li>Complete source code (React + Python + MongoDB) — no lock-in</li>
                <li>Private hosting on Emergent's cloud infrastructure</li>
                <li>AI assistant to make changes by just describing what you need</li>
                <li>GitHub integration for version control and backup</li>
                <li>Deploy updates instantly — no waiting for developers</li>
              </ul>
            </div>
          </div>

          <a href="https://emergent.sh" target="_blank" rel="noopener noreferrer" data-testid="emergent-signup-btn" className="block w-full text-center py-3 bg-[#D35400] hover:bg-[#A04000] text-white font-bold rounded-lg text-sm transition-colors">
            Get Started at emergent.sh
          </a>
        </div>
        </div>
      )}

      {/* Email SMTP Tab */}
      {tab === 'email' && (
        <div className="space-y-4">
          <div className="bg-white border border-[#E2E2DF] rounded-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3"><Mail size={20} className="text-[#4A6FA5]" /><h3 className="text-lg font-semibold text-slate-800 font-[Manrope]">SMTP Email Settings</h3></div>
              <span className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${emailForm.configured ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                {emailForm.configured ? <><CheckCircle size={12} /> Configured</> : <><XCircle size={12} /> Not Configured</>}
              </span>
            </div>
            <form onSubmit={saveEmail} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { k: 'smtp_host', l: 'SMTP Host', p: 'smtp.gmail.com' },
                  { k: 'smtp_port', l: 'SMTP Port', p: '465', t: 'number' },
                  { k: 'smtp_user', l: 'SMTP Username', p: 'your@email.com' },
                  { k: 'smtp_password', l: 'SMTP Password', p: 'App password', t: 'password' },
                  { k: 'from_email', l: 'From Email', p: 'noreply@rtr.co.nz' },
                  { k: 'from_name', l: 'From Name', p: "Roofs To Remedy Ltd" },
                  { k: 'bcc_copy_to', l: 'Copy every email to (hidden copy)', p: 'quotes@roofstoremedy.co.nz' },
                  { k: 'redirect_all_to', l: 'Test mode: send ALL email only to', p: 'Leave blank on the live site' },
                ].map(f => (
                  <div key={f.k}>
                    <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">{f.l}</label>
                    <input data-testid={`email-${f.k}`} type={f.t || 'text'} placeholder={f.p} value={emailForm[f.k] || ''} onChange={e => setEmailForm({...emailForm, [f.k]: f.t === 'number' ? parseInt(e.target.value) || 465 : e.target.value})} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]" />
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <button type="submit" data-testid="email-save-btn" disabled={saving} className={`flex items-center gap-2 px-5 py-2 rounded-md text-sm font-semibold transition-all ${saved ? 'bg-green-500 text-white' : 'bg-[#4A6FA5] hover:bg-[#3A5A8A] text-white'} disabled:opacity-50`}><Save size={14} /> {saved ? 'Saved!' : 'Save Settings'}</button>
                <button type="button" data-testid="email-test-btn" onClick={testEmail} disabled={testingEmail} className="flex items-center gap-2 px-4 py-2 border border-[#E2E2DF] rounded-md text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"><Send size={14} /> {testingEmail ? 'Sending…' : 'Test Email'}</button>
              </div>
            </form>
          </div>
          <div className="bg-slate-50 border border-[#E2E2DF] rounded-md p-5 text-xs text-slate-600 space-y-4">
            <div>
              <h4 className="text-sm font-bold text-slate-800 mb-2">Enlighten Designs Hosted Email Setup Guide</h4>
              <div className="space-y-3">
                <div className="bg-white rounded-md border border-blue-200 p-2.5 text-[11px] text-blue-900">
                  <b>Set up</b> — server <code>mail.enlightenhosting.com</code>, port <code>465</code> (SSL), mailbox <code>quotes@roofstoremedy.co.nz</code>. Leave the password box empty to keep the saved password. <b>Test mode</b>: when filled in, every email goes only to that address (the subject shows who it was meant for). Use it on the preview; leave it blank on the live site.
                </div>
                <div>
                  <p className="font-bold text-slate-700 mb-1">Step 1: Ask Enlighten Designs for the outbound SMTP details</p>
                  <p>Copy the email in the chat above and send to your Enlighten contact. Request:</p>
                  <ul className="list-disc ml-5 mt-1 space-y-0.5">
                    <li>SMTP outbound port: <b>465</b> SSL (confirmed working)</li>
                    <li>The mailbox password for <code>quotes@roofstoremedy.co.nz</code></li>
                    <li>SPF / DKIM DNS record confirmation on <code>roofstoremedy.co.nz</code></li>
                    <li>Daily outbound send limit (some cPanel hosts cap at ~500/day)</li>
                  </ul>
                </div>
                <div>
                  <p className="font-bold text-slate-700 mb-1">Step 2: When their reply arrives, paste the password + click Save</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-1 bg-white rounded-md border border-slate-200 p-3">
                    <span className="text-slate-500">SMTP Host:</span><span className="font-mono font-semibold">mail.enlightenhosting.com <span className="text-emerald-600 text-[11px]">✓ pre-filled</span></span>
                    <span className="text-slate-500">SMTP Port:</span><span className="font-mono font-semibold">465 <span className="text-emerald-600 text-[11px]">✓ confirmed</span></span>
                    <span className="text-slate-500">SMTP Username:</span><span className="font-semibold">quotes@roofstoremedy.co.nz <span className="text-emerald-600 text-[11px]">✓ pre-filled</span></span>
                    <span className="text-slate-500">SMTP Password:</span><span className="font-semibold text-orange-700">Paste the mailbox password (from Enlighten) &larr; <em>only field you need to type</em></span>
                    <span className="text-slate-500">From Email:</span><span className="font-semibold">quotes@roofstoremedy.co.nz <span className="text-emerald-600 text-[11px]">✓ pre-filled</span></span>
                    <span className="text-slate-500">From Name:</span><span className="font-semibold">Roofs To Remedy Ltd <span className="text-emerald-600 text-[11px]">✓ pre-filled</span></span>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 italic">If Test Email fails: (1) check the port — try <code>465</code> if <code>587</code> rejects; (2) verify the password from Enlighten hasn&#39;t expired; (3) ask Enlighten to confirm the mailbox has outbound SMTP enabled.</p>
              </div>
            </div>
            <hr className="border-slate-200" />
            <div className="space-y-1">
              <p><strong>M365 alternative:</strong> Use <code className="bg-white px-1 py-0.5 rounded border border-slate-200 font-mono text-[11px]">smtp.office365.com</code> port 587 + an App Password from <a href="https://mysignins.microsoft.com/security-info" target="_blank" rel="noopener noreferrer" className="text-[#4A6FA5] underline">mysignins.microsoft.com/security-info</a> (only if we migrate to Microsoft 365 in the future).</p>
              <p><strong>Gmail alternative:</strong> Use <code className="bg-white px-1 py-0.5 rounded border border-slate-200 font-mono text-[11px]">smtp.gmail.com</code> port 587 + a Google App Password from <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-[#4A6FA5] underline">myaccount.google.com/apppasswords</a> (requires 2FA on the Google account first).</p>
              <p><strong>Custom provider:</strong> Contact your email provider for SMTP host, port, and credentials.</p>
            </div>
          </div>
        </div>
      )}

      {/* SMS · TNZ Group NZ · Go-Live 2026-02-27 (swapped from
          MessageMedia/Sinch → TNZ because TNZ has same-day self-serve NZ
          activation. Bearer-token auth, no separate api_secret needed). */}
      {tab === 'sms' && (
        <div className="space-y-4">
          <div className="bg-white border border-[#E2E2DF] rounded-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3"><MessageSquare size={20} className="text-[#2F855A]" /><h3 className="text-lg font-semibold text-slate-800 font-[Manrope]">TNZ Group NZ · SMS Settings</h3></div>
              <span className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${smsForm.configured ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                {smsForm.configured ? <><CheckCircle size={12} /> Configured</> : <><XCircle size={12} /> Not Configured</>}
              </span>
            </div>
            <form onSubmit={saveSms} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { k: 'api_token', l: 'API Token (Bearer)', p: smsForm.api_token_set ? '••••••••• (saved · re-paste to change)' : 'From TNZ Dashboard → API → Auth Token', t: 'password' },
                  { k: 'from_number', l: 'Sender Number (optional)', p: 'Leave blank to use TNZ short code, or paste +6421616739 / "RTR Ltd"' },
                ].map(f => (
                  <div key={f.k}>
                    <label className="block text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">{f.l}</label>
                    <input data-testid={`sms-${f.k}`} type={f.t || 'text'} placeholder={f.p} value={smsForm[f.k] || ''} onChange={e => setSmsForm({...smsForm, [f.k]: e.target.value})} className="w-full px-3 py-2 border border-[#E2E2DF] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#2F855A]" />
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-3">
                <button type="submit" disabled={saving} className={`flex items-center gap-2 px-5 py-2 rounded-md text-sm font-semibold transition-all ${saved ? 'bg-green-500 text-white' : 'bg-[#2F855A] hover:bg-[#276749] text-white'} disabled:opacity-50`}><Save size={14} /> {saved ? 'Saved!' : 'Save Settings'}</button>
                <button type="button" data-testid="sms-test-btn" onClick={testSms} className="flex items-center gap-2 px-4 py-2 border border-[#E2E2DF] rounded-md text-sm text-slate-600 hover:bg-slate-50"><Send size={14} /> Test SMS</button>
              </div>
              {testResult && (
                <div className={`text-xs rounded-md p-2 ${
                  testResult.status === 'sent' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' :
                  testResult.status === 'logged' ? 'bg-slate-50 text-slate-700 border border-slate-200' :
                  testResult.status === 'skipped_international' ? 'bg-amber-50 text-amber-800 border border-amber-200' :
                  'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  <b>Test result:</b> {testResult.status} · {testResult.message}
                  {testResult.message_id && <span> · id: <code>{testResult.message_id}</code></span>}
                </div>
              )}
            </form>
          </div>
          <div className="bg-slate-50 border border-[#E2E2DF] rounded-md p-4 text-xs text-slate-500 space-y-2">
            <p><strong>Where to get the token</strong> (5 minutes):</p>
            <ol className="list-decimal ml-5 space-y-0.5">
              <li>Log into your TNZ Group dashboard: <a href="https://www.tnz.co.nz/Login/" target="_blank" rel="noopener noreferrer" className="text-[#2F855A] underline">tnz.co.nz/Login</a></li>
              <li>Navigate to <strong>Settings → API</strong> (or <strong>Integrations → API</strong>)</li>
              <li>Ensure <strong>&quot;Enable API Access&quot;</strong> is ticked for your user</li>
              <li>Click <strong>Generate Auth Token</strong> — copy the resulting token (long string, may look like <code>AbCdEf...xyz</code>)</li>
              <li>Paste it into the <strong>API Token</strong> field above and click Save</li>
              <li>Click <strong>Test SMS</strong> → enter your mobile <code>+6421616739</code> → verify the SMS arrives</li>
            </ol>
            <p className="pt-2"><strong>Sender Number:</strong> Leave blank on first setup to use TNZ&#39;s default assigned short code (cheapest). Later you can:</p>
            <ul className="list-disc ml-5 space-y-0.5">
              <li>Paste <code>+6421616739</code> (Trevor&#39;s mobile) — clients can reply back to Trevor directly</li>
              <li>Paste an alphanumeric like <code>&quot;RTR Ltd&quot;</code> (11 chars max) — nicer branding but no reply-back</li>
              <li>Purchase a dedicated NZ shortcode via TNZ (~NZD $30-100/mo) — full 2-way SMS via TNZ inbox</li>
            </ul>
            <p className="pt-2"><strong>NZ-only for Go-Live:</strong> International numbers (any non-<code>+64</code>) are logged but NOT sent — use email for overseas clients. TNZ can send internationally with a plan upgrade if needed post Go-Live.</p>
            <p>SMS will be logged (not sent) until a valid token is saved.</p>
          </div>
        </div>
      )}

      {/* Quote Types Tab */}
      {tab === 'quote-types' && <QuoteTypesEditor api={api} initialFilter={tabFilter} />}

      {/* Data Linkage Map — visualizes how Roof Details, Appraisals,
          Quote Types, Builders, Components and PDFs link together
          + flags missing classifications and orphan records. */}
      {tab === 'data-linkage' && <DataLinkageMap />}

      {/* Pipe Usages Tab — operator-managed list of penetration types
          shown on Pipe / Flue pin edit in the Roof Diagram Annotator. */}
      {tab === 'pipe-usages' && <PipeUsagesEditor api={api} />}

      {/* Flashing Categories Tab — operator-managed Back tray / Apron /
          Barge / Custom / etc. categories that drive the Flashing
          Template "Category" picker. Renames cascade to templates so
          existing records keep showing in the right bucket. */}
      {tab === 'flashing-categories' && <FlashingCategoriesEditor api={api} />}

      {/* Stripping Rates — per-re-roof-type admin overrides for
          stripping labour, waste m³ per m², and scrap value per m². */}
      {tab === 'stripping-rates' && <StrippingRatesEditor api={api} />}

      {/* Skip Bin Suppliers — admin-managed dump-bin supplier directory
          with bin sizes + per-bin pricing + delivery lead days. */}
      {tab === 'skip-bin-suppliers' && <SkipBinSuppliersEditor api={api} />}

      {/* Cultural Phrases — per-culture greeting / thank-you / farewell. */}
      {tab === 'cultural-phrases' && <CulturalPhrasesEditor api={api} />}

      {/* Cultural Celebrations — per-culture key dates with reminder lead. */}
      {tab === 'cultural-celebrations' && <CulturalCelebrationsEditor api={api} />}

      {/* Defect Catalogue — per-roof-subtype defects with labour/materials
          rates. Drives the Repair + Restoration quote builders. */}
      {tab === 'defect-catalogue' && <DefectCatalogueEditor api={api} />}

      {/* Paint Systems — per-roof-subtype paint coat sequences ($/m²).
          Drives the Stage 2 Painting section of the Restoration quote. */}
      {tab === 'paint-systems' && <PaintSystemsEditor api={api} />}
      {tab === 'save-templates' && <SaveTemplatesEditor api={api} />}

      {/* Test Result */}
      {testResult && (
        <div className={`p-4 rounded-md text-sm ${testResult.status === 'sent' ? 'bg-green-50 text-green-700 border border-green-200' : testResult.status === 'logged' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-testid="test-result">
          <strong>{testResult.status === 'sent' ? 'Sent!' : testResult.status === 'logged' ? 'Logged' : 'Failed'}:</strong> {testResult.message}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quote Types editor — pulls from /api/quote-types and lets users edit the
// description (bullet list under "Our Quotation Includes the FOLLOWING:") and
// details (numbered notes under "Notes:") for each quote type. Used by the
// Quotation Letter PDF generator.
// ---------------------------------------------------------------------------
function QuoteTypesEditor({ api, initialFilter }) {
  const [types, setTypes] = useState([]);
  const [activeRef, setActiveRef] = useState('');
  const [form, setForm] = useState({ ref: '', description: '', details: '', completion_description: '', completion_details: '', roof_types: [], quote_categories: [] });
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  // Classification (roof_type + quote_category) auto-saves on every
  // select change. We keep the last "snapshot before edit" in an undo
  // ref so the operator can revert with one click within the 8-second
  // window. classifFlash is the small pill that surfaces "Saved · undo".
  const undoSnapshotRef = useRef(null);
  const [classifFlash, setClassifFlash] = useState(null); // { msg, undoable }
  const classifFlashTimerRef = useRef(null);
  // Auto-save for free-text fields (description / details / completion_*).
  // Debounced — fires 900ms after the last keystroke so we don't
  // hammer the backend on every character. Mirrors classifFlash for
  // the "Auto-saved · Undo" pill. textUndoSnapshotRef captures the
  // doc state BEFORE the in-flight edit so undo can revert.
  const textUndoSnapshotRef = useRef(null);
  const [textFlash, setTextFlash] = useState(null);
  const textFlashTimerRef = useRef(null);
  const textDebounceRef = useRef(null);
  const [showNew, setShowNew] = useState(false);
  const [newRef, setNewRef] = useState('');
  // Enumerated picklists for roof_type / quote_category. Loaded once
  // from the backend so we have a single source of truth — the same
  // /api/quote-types-enums endpoint feeds the Components-tab Quote
  // Type picker.
  const [enums, setEnums] = useState({ roof_types: [], quote_categories: [] });

  const reload = useCallback(async (selectRef) => {
    // Pull both: full quote_types records (with description/details) AND the
    // dropdown_options.quote_types list (so every selectable type shows up
    // in the sidebar even if it doesn't have a script yet).
    const [r, ddRes, enRes] = await Promise.all([
      api.get('/quote-types'),
      api.get('/dropdowns/quote_types').catch(() => ({ data: [] })),
      api.get('/quote-types-enums').catch(() => ({ data: { roof_types: [], quote_categories: [] } })),
    ]);
    setEnums(enRes.data || { roof_types: [], quote_categories: [] });
    const records = (r.data || []).map(d => ({
      ...d,
      roof_types: Array.isArray(d.roof_types) ? d.roof_types : (d.roof_type ? [d.roof_type] : []),
      quote_categories: Array.isArray(d.quote_categories) ? d.quote_categories : (d.quote_category ? [d.quote_category] : []),
    }));
    const recordRefs = new Set(records.map(t => t.ref));
    const ddList = Array.isArray(ddRes.data) ? ddRes.data : (ddRes.data?.options || []);
    const stubs = ddList.filter(ref => !recordRefs.has(ref)).map(ref => ({ ref, description: '', details: '', completion_description: '', completion_details: '', roof_types: [], quote_categories: [], _stub: true }));
    const merged = [...records, ...stubs].sort((a, b) => a.ref.localeCompare(b.ref));
    setTypes(merged);
    const target = selectRef || (merged[0] && merged[0].ref) || '';
    setActiveRef(target);
    const found = merged.find(t => t.ref === target);
    setForm(found || { ref: target, description: '', details: '', completion_description: '', completion_details: '', roof_types: [], quote_categories: [] });
  }, [api]);

  useEffect(() => { reload(); }, [reload]);

  // Auto-save Roof Types / Quote Categories on every toggle.
  // the PUT we already do for the full Save button but scoped to the
  // two classification fields (now multi-value arrays) so the operator
  // never has to remember to click Save before moving on. Snapshots
  // the previous arrays so the "undo" pill can revert with a single
  // click.
  const autoSaveClassification = useCallback(async (nextRoofTypes, nextCats) => {
    if (!activeRef) return;
    undoSnapshotRef.current = {
      ref: activeRef,
      roof_types: [...(form.roof_types || [])],
      quote_categories: [...(form.quote_categories || [])],
    };
    setForm((prev) => ({ ...prev, roof_types: nextRoofTypes, quote_categories: nextCats }));
    try {
      await api.put(`/quote-types/${encodeURIComponent(activeRef)}`, {
        roof_types: nextRoofTypes,
        quote_categories: nextCats,
      });
      setTypes((prev) => prev.map(t => t.ref === activeRef
        ? { ...t, roof_types: nextRoofTypes, quote_categories: nextCats, _stub: false }
        : t));
      if (classifFlashTimerRef.current) clearTimeout(classifFlashTimerRef.current);
      setClassifFlash({ msg: 'Saved', undoable: true });
      classifFlashTimerRef.current = setTimeout(() => setClassifFlash(null), 8000);
    } catch (e) {
      console.error('Auto-save failed', e);
      setClassifFlash({ msg: 'Save failed', undoable: false });
    }
  }, [api, activeRef, form.roof_types, form.quote_categories]);

  const undoLastClassification = useCallback(async () => {
    const snap = undoSnapshotRef.current;
    if (!snap) return;
    undoSnapshotRef.current = null;
    if (classifFlashTimerRef.current) clearTimeout(classifFlashTimerRef.current);
    setClassifFlash(null);
    if (snap.ref !== activeRef) {
      try {
        await api.put(`/quote-types/${encodeURIComponent(snap.ref)}`, {
          roof_types: snap.roof_types, quote_categories: snap.quote_categories,
        });
        setTypes((prev) => prev.map(t => t.ref === snap.ref
          ? { ...t, roof_types: snap.roof_types, quote_categories: snap.quote_categories }
          : t));
      } catch (e) { console.error('Undo failed', e); }
      return;
    }
    setForm((prev) => ({ ...prev, roof_types: snap.roof_types, quote_categories: snap.quote_categories }));
    try {
      await api.put(`/quote-types/${encodeURIComponent(activeRef)}`, {
        roof_types: snap.roof_types, quote_categories: snap.quote_categories,
      });
      setTypes((prev) => prev.map(t => t.ref === activeRef
        ? { ...t, roof_types: snap.roof_types, quote_categories: snap.quote_categories }
        : t));
    } catch (e) { console.error('Undo failed', e); }
  }, [api, activeRef]);

  const switchTo = (ref) => {
    undoSnapshotRef.current = null;
    if (classifFlashTimerRef.current) clearTimeout(classifFlashTimerRef.current);
    setClassifFlash(null);
    textUndoSnapshotRef.current = null;
    if (textFlashTimerRef.current) clearTimeout(textFlashTimerRef.current);
    if (textDebounceRef.current) clearTimeout(textDebounceRef.current);
    setTextFlash(null);
    setActiveRef(ref);
    const found = types.find(t => t.ref === ref);
    setForm(found || { ref, description: '', details: '', completion_description: '', completion_details: '', roof_types: [], quote_categories: [] });
  };

  // Auto-save for the free-text fields. Called by every textarea's
  // onChange with the next form-state. Captures the doc as it was at
  // the moment edit started so Undo can revert to that snapshot, then
  // debounces the PUT for 900 ms so we don't hammer the backend.
  const autoSaveText = useCallback((nextForm) => {
    setForm(nextForm);
    // First keystroke after a fresh switchTo captures the snapshot.
    if (!textUndoSnapshotRef.current) {
      textUndoSnapshotRef.current = {
        ref: activeRef,
        description: form.description || '',
        details: form.details || '',
        completion_description: form.completion_description || '',
        completion_details: form.completion_details || '',
      };
    }
    if (textDebounceRef.current) clearTimeout(textDebounceRef.current);
    textDebounceRef.current = setTimeout(async () => {
      try {
        await api.put(`/quote-types/${encodeURIComponent(activeRef)}`, {
          description: nextForm.description || '',
          details: nextForm.details || '',
          completion_description: nextForm.completion_description || '',
          completion_details: nextForm.completion_details || '',
        });
        setTypes((prev) => prev.map(t => t.ref === activeRef
          ? { ...t, description: nextForm.description || '', details: nextForm.details || '',
              completion_description: nextForm.completion_description || '',
              completion_details: nextForm.completion_details || '' }
          : t));
        if (textFlashTimerRef.current) clearTimeout(textFlashTimerRef.current);
        setTextFlash({ msg: 'Saved', undoable: true });
        textFlashTimerRef.current = setTimeout(() => setTextFlash(null), 8000);
      } catch (e) {
        console.error('Auto-save text failed', e);
        setTextFlash({ msg: 'Save failed', undoable: false });
      }
    }, 900);
  }, [api, activeRef, form.description, form.details, form.completion_description, form.completion_details]);

  const undoLastText = useCallback(async () => {
    const snap = textUndoSnapshotRef.current;
    if (!snap || snap.ref !== activeRef) return;
    textUndoSnapshotRef.current = null;
    if (textFlashTimerRef.current) clearTimeout(textFlashTimerRef.current);
    if (textDebounceRef.current) clearTimeout(textDebounceRef.current);
    setTextFlash(null);
    setForm((prev) => ({ ...prev,
      description: snap.description, details: snap.details,
      completion_description: snap.completion_description, completion_details: snap.completion_details,
    }));
    try {
      await api.put(`/quote-types/${encodeURIComponent(activeRef)}`, {
        description: snap.description, details: snap.details,
        completion_description: snap.completion_description, completion_details: snap.completion_details,
      });
      setTypes((prev) => prev.map(t => t.ref === activeRef
        ? { ...t, description: snap.description, details: snap.details,
            completion_description: snap.completion_description, completion_details: snap.completion_details }
        : t));
    } catch (e) { console.error('Undo failed', e); }
  }, [api, activeRef]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/quote-types/${encodeURIComponent(activeRef)}`, {
        description: form.description,
        details: form.details,
        completion_description: form.completion_description || '',
        completion_details: form.completion_details || '',
        roof_types: form.roof_types || [],
        quote_categories: form.quote_categories || [],
      });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
      await reload(activeRef);
    } finally {
      setSaving(false);
    }
  };

  const addNew = async () => {
    const ref = newRef.trim();
    if (!ref) return;
    await api.put(`/quote-types/${encodeURIComponent(ref)}`, { description: '', details: '', completion_description: '', completion_details: '', roof_types: [], quote_categories: [] });
    setShowNew(false);
    setNewRef('');
    await reload(ref);
  };

  const remove = async (ref) => {
    if (!window.confirm(`Delete quote type "${ref}"? This cannot be undone.`)) return;
    await api.delete(`/quote-types/${encodeURIComponent(ref)}`);
    await reload();
  };

  // Sidebar filter (set by Audit "Jump to fix" deep-link or by the
  // operator clicking the filter chips below). Filters the sidebar
  // list down to only un-classified quote types so the operator can
  // tear through them without scrolling past already-done refs.
  const [sideFilter, setSideFilter] = useState(initialFilter || '');

  const filteredTypes = sideFilter === 'missing-roof'
    ? types.filter(t => !(t.roof_types || []).length)
    : sideFilter === 'missing-category'
      ? types.filter(t => !(t.quote_categories || []).length)
      : sideFilter === 'unclassified'
        ? types.filter(t => !(t.roof_types || []).length || !(t.quote_categories || []).length)
        : types;

  return (
    <div data-testid="quote-types-editor" className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4">
      {/* Sidebar: list of quote types */}
      <div className="bg-white border border-[#E2E2DF] rounded-md p-3 space-y-1 self-start">
        <div className="flex items-center justify-between pb-2 mb-1 border-b border-[#E2E2DF]">
          <h4 className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">Quote types</h4>
          <button data-testid="quote-type-new" onClick={() => setShowNew(true)} className="text-[#D35400] hover:bg-orange-50 p-1 rounded transition-colors" title="Add new quote type"><Plus size={14} /></button>
        </div>

        {/* Audit filter chips — quickly narrow the list when chasing
            specific gaps. The "Jump to fix" link from Audit pre-selects
            the right one. */}
        <div className="flex flex-wrap gap-1 pb-2 mb-1 border-b border-[#E2E2DF]" data-testid="qt-side-filter">
          {[
            { key: '', label: `All (${types.length})` },
            { key: 'missing-roof', label: `Missing roof (${types.filter(t => !(t.roof_types || []).length).length})` },
            { key: 'missing-category', label: `Missing cat (${types.filter(t => !(t.quote_categories || []).length).length})` },
          ].map(f => (
            <button
              key={f.key || 'all'}
              data-testid={`qt-side-filter-${f.key || 'all'}`}
              onClick={() => setSideFilter(f.key)}
              className={`text-[10px] px-2 py-0.5 rounded-full ring-1 font-semibold ${
                sideFilter === f.key
                  ? 'bg-[#D35400] text-white ring-[#A04000]'
                  : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'
              }`}
            >{f.label}</button>
          ))}
        </div>

        {showNew && (
          <div className="flex items-center gap-1 mb-2">
            <input autoFocus value={newRef} onChange={e => setNewRef(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addNew(); if (e.key === 'Escape') setShowNew(false); }} placeholder="e.g. Replace Tile" className="flex-1 text-xs px-2 py-1 border border-[#E2E2DF] rounded" />
            <button onClick={addNew} className="text-[10px] font-bold text-[#D35400] hover:text-[#A04000] px-1.5 py-1">Add</button>
          </div>
        )}
        {types.length === 0 && <p className="text-xs text-slate-500 italic">Loading…</p>}
        {filteredTypes.length === 0 && types.length > 0 && (
          <p className="text-xs text-emerald-700 italic px-1 py-2">✓ all clean for this filter</p>
        )}
        {filteredTypes.map(t => (
          <div key={t.ref} className="flex items-center group">
            <button data-testid={`qt-pick-${t.ref}`} onClick={() => switchTo(t.ref)} className={`flex-1 text-left text-sm px-2 py-1.5 rounded transition-colors flex items-center gap-1.5 ${activeRef === t.ref ? 'bg-[#FFF7ED] text-[#A04000] font-semibold' : 'text-slate-700 hover:bg-slate-50'}`}>
              <span className="flex-1">{t.ref}</span>
              {t._stub && <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400" title="No script yet — using fallback message in PDF">empty</span>}
            </button>
            <button onClick={() => remove(t.ref)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-rose-600 p-1 transition-opacity" title="Delete"><Trash2 size={12} /></button>
          </div>
        ))}
      </div>

      {/* Editor pane */}
      {activeRef && (
        <form onSubmit={save} className="bg-white border border-[#E2E2DF] rounded-md p-6 space-y-4">
          {/* Sticky top bar — title + Save + Auto-saved · Undo pill.
              Moved up here per operator request so the Save button is
              reachable without scrolling on long description bodies. */}
          <div className="sticky top-0 -mx-6 px-6 -mt-6 pt-4 pb-3 mb-2 bg-white border-b border-[#E2E2DF] z-10 flex items-center gap-3 flex-wrap">
            <FileText size={20} className="text-[#D35400]" />
            <h3 className="text-lg font-semibold text-slate-800 font-[Manrope] truncate">{activeRef}</h3>
            <button data-testid="qt-save-top" type="submit" disabled={saving} className="ml-auto flex items-center gap-1.5 text-xs font-bold text-white bg-[#D35400] hover:bg-[#A04000] px-3 py-1.5 rounded-md transition-colors disabled:opacity-50">
              <Save size={12} /> {saving ? 'Saving…' : 'Save'}
            </button>
            {textFlash && (
              <div className="inline-flex items-center gap-1.5 text-[11px]" data-testid="qt-text-flash">
                {textFlash.msg === 'Saved' ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 font-semibold">✓ Auto-saved</span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 ring-1 ring-rose-200 font-semibold">⚠ {textFlash.msg}</span>
                )}
                {textFlash.undoable && textUndoSnapshotRef.current && (
                  <button type="button" data-testid="qt-text-undo" onClick={undoLastText} className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-300 font-semibold hover:bg-amber-100">↶ Undo</button>
                )}
              </div>
            )}
            {savedFlash && <span className="text-[11px] text-emerald-600 font-semibold">✓ Saved</span>}
          </div>

          {/* Classification fields — drive the Components-tab Quote Type
              picker's cascade. roof_types narrows materials; quote_categories
              flags this entry under one or more Builder categories
              (Reroof / Repairs / Restoration / etc.). Multi-select:
              click a chip to toggle on / off. Auto-saves on every
              toggle with an 8-second Undo pill. Empty arrays default
              to no narrowing so existing rows behave as before. */}
          <div className="pb-3 border-b border-[#E2E2DF]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 block mb-1.5">Roof Types <span className="text-slate-400 font-normal">(toggle one or more)</span></label>
                <div className="flex flex-wrap gap-1.5">
                  {(enums.roof_types || []).map(rt => {
                    const selected = (form.roof_types || []).includes(rt);
                    return (
                      <button
                        key={rt}
                        type="button"
                        data-testid={`qt-roof-type-${rt.replace(/\s+/g, '-').toLowerCase()}`}
                        onClick={() => {
                          const current = form.roof_types || [];
                          const next = selected
                            ? current.filter(x => x !== rt)
                            : [...current, rt];
                          autoSaveClassification(next, form.quote_categories || []);
                        }}
                        className={`text-xs px-2.5 py-1 rounded-full font-semibold transition-colors ring-1 ${
                          selected
                            ? 'bg-violet-600 text-white ring-violet-700 hover:bg-violet-700'
                            : 'bg-white text-slate-600 ring-slate-200 hover:bg-violet-50 hover:text-violet-800 hover:ring-violet-200'
                        }`}
                      >
                        {selected ? '✓ ' : ''}{rt}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-slate-400 mt-1.5">Narrows Primary Material + material chips on the Components tab.</p>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 block mb-1.5">Quote Categories <span className="text-slate-400 font-normal">(toggle one or more)</span></label>
                <div className="flex flex-wrap gap-1.5">
                  {(enums.quote_categories || []).map(c => {
                    const selected = (form.quote_categories || []).includes(c);
                    return (
                      <button
                        key={c}
                        type="button"
                        data-testid={`qt-quote-category-${c.replace(/\s+/g, '-').toLowerCase()}`}
                        onClick={() => {
                          const current = form.quote_categories || [];
                          const next = selected
                            ? current.filter(x => x !== c)
                            : [...current, c];
                          autoSaveClassification(form.roof_types || [], next);
                        }}
                        className={`text-xs px-2.5 py-1 rounded-full font-semibold transition-colors ring-1 ${
                          selected
                            ? 'bg-[#D35400] text-white ring-[#A04000] hover:bg-[#A04000]'
                            : 'bg-white text-slate-600 ring-slate-200 hover:bg-orange-50 hover:text-orange-800 hover:ring-orange-200'
                        }`}
                      >
                        {selected ? '✓ ' : ''}{c}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-slate-400 mt-1.5">Maps to the Builder subtab (Repairs / Reroof / Restoration / Maintenance / Guttering / …).</p>
              </div>
            </div>
            {/* Auto-save status pill — appears for 8 s after each change. */}
            {classifFlash && (
              <div className="mt-3 inline-flex items-center gap-2 text-[11px]" data-testid="qt-classif-flash">
                {classifFlash.msg === 'Saved' ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 font-semibold">
                    ✓ Auto-saved
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 ring-1 ring-rose-200 font-semibold">
                    ⚠ {classifFlash.msg}
                  </span>
                )}
                {classifFlash.undoable && undoSnapshotRef.current && (
                  <button
                    type="button"
                    data-testid="qt-classif-undo"
                    onClick={undoLastClassification}
                    className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-300 font-semibold hover:bg-amber-100"
                    title={`Revert to: [${(undoSnapshotRef.current.roof_types || []).join(', ') || 'none'}] / [${(undoSnapshotRef.current.quote_categories || []).join(', ') || 'none'}]`}
                  >
                    ↶ Undo
                  </button>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 block mb-1">Description (Our Quotation Includes the FOLLOWING)</label>
            <p className="text-[11px] text-slate-500 mb-2">Use <code className="bg-slate-100 px-1 rounded">• </code> at the start of a line for bullets. Blank lines = paragraph break.</p>
            <textarea data-testid="qt-description" rows={9} value={form.description || ''} onChange={e => autoSaveText({ ...form, description: e.target.value })} className="w-full text-sm px-3 py-2 border border-[#E2E2DF] rounded-md font-mono leading-relaxed" />
          </div>

          <div>
            <label className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 block mb-1">Details (Notes)</label>
            <p className="text-[11px] text-slate-500 mb-2">Use <code className="bg-slate-100 px-1 rounded">1. </code> / <code className="bg-slate-100 px-1 rounded">2. </code> at the start of a line for numbered notes.</p>
            <textarea data-testid="qt-details" rows={7} value={form.details || ''} onChange={e => autoSaveText({ ...form, details: e.target.value })} className="w-full text-sm px-3 py-2 border border-[#E2E2DF] rounded-md font-mono leading-relaxed" />
          </div>

          <div className="pt-2 border-t border-[#E2E2DF]">
            <h4 className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 mb-3">Completion / Sign-off scripts</h4>
            <p className="text-[11px] text-slate-500 mb-3 -mt-2">Used on the Completion / Hand-over PDF after the work is finished. Leave blank if not applicable for this quote type.</p>

            <label className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 block mb-1">Completion Description</label>
            <textarea data-testid="qt-completion-description" rows={5} value={form.completion_description || ''} onChange={e => autoSaveText({ ...form, completion_description: e.target.value })} className="w-full text-sm px-3 py-2 border border-[#E2E2DF] rounded-md font-mono leading-relaxed mb-4" />

            <label className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500 block mb-1">Completion Details</label>
            <textarea data-testid="qt-completion-details" rows={5} value={form.completion_details || ''} onChange={e => autoSaveText({ ...form, completion_details: e.target.value })} className="w-full text-sm px-3 py-2 border border-[#E2E2DF] rounded-md font-mono leading-relaxed" />
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button data-testid="qt-save" type="submit" disabled={saving} className="flex items-center gap-1.5 text-xs font-bold text-white bg-[#D35400] hover:bg-[#A04000] px-4 py-2 rounded-md transition-colors disabled:opacity-50">
              <Save size={13} /> {saving ? 'Saving…' : 'Save'}
            </button>
            {savedFlash && <span className="text-xs text-emerald-600 font-semibold">✓ Saved</span>}
          </div>
        </form>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Pipe Usages editor — operator-managed registry of penetration usage
// types (Hotwater / Toilet Breather / Breather 40 / Breather 60 / …) that
// drive the Pipe-pin auto-link flow on the Roof Diagram Annotator. Each
// usage can optionally carry a default diameter (mm) so picking the
// usage on a pin auto-fills the diameter. Inline-edit on click with
// debounced auto-save (~900 ms) + 8 s Undo pill. Seeds are protected
// from delete; key renames cascade through flashing_templates.
// ---------------------------------------------------------------------------
function PipeUsagesEditor({ api }) {
  const [rows, setRows] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [newRow, setNewRow] = useState({ label: '', diameter_mm: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // Linkage dropdown options (Dekite + Back-Tray flashing templates).
  // Loaded once on mount + after add — surfaces under the per-row
  // dropdowns. Empty arrays render a "No options yet" hint so the
  // operator knows to seed flashing_templates first.
  const [linkOptions, setLinkOptions] = useState({ dekite: [], dekite_std: [], dekite_retrofit: [], dekite_lead: [], btray: [] });
  // Inline-edit auto-save state — pendingByRow tracks the draft for
  // each row, undoSnapshotByRow tracks the previous saved doc per
  // row so the operator can revert with one click. flashByRow drives
  // the per-row "✓ Auto-saved · Undo" pill.
  const [flashByRow, setFlashByRow] = useState({});
  const debounceRef = useRef({});
  const undoSnapshotRef = useRef({});
  const flashTimerRef = useRef({});

  const reload = useCallback(async () => {
    const [r, lo] = await Promise.all([
      api.get('/pipe-usages'),
      api.get('/pipe-usages/linkage-options').catch(() => ({ data: { dekite: [], dekite_std: [], dekite_retrofit: [], dekite_lead: [], btray: [] } })),
    ]);
    setRows(Array.isArray(r.data) ? r.data : []);
    setLinkOptions(lo.data || { dekite: [], dekite_std: [], dekite_retrofit: [], dekite_lead: [], btray: [] });
  }, [api]);

  useEffect(() => { reload(); }, [reload]);

  // Auto-save one field for one row. Debounced 900 ms. Captures the
  // current row state on first edit so Undo can revert. Re-uses the
  // same per-row keyed maps as the Quote Types editor.
  const autoSaveField = useCallback((rowId, currentRow, fieldName, nextValue) => {
    // Local optimistic update first.
    setRows((prev) => prev.map((r) => r._id === rowId ? { ...r, [fieldName]: nextValue } : r));
    if (!undoSnapshotRef.current[rowId]) {
      undoSnapshotRef.current[rowId] = { ...currentRow };
    }
    if (debounceRef.current[rowId]) clearTimeout(debounceRef.current[rowId]);
    debounceRef.current[rowId] = setTimeout(async () => {
      try {
        // Build the patch body — only the field that changed.
        const body = { [fieldName]: nextValue };
        // diameter_mm needs the int conversion the backend expects.
        if (fieldName === 'diameter_mm') {
          body.diameter_mm = (nextValue === '' || nextValue === null) ? null : parseInt(nextValue, 10);
        }
        await api.put(`/pipe-usages/${rowId}`, body);
        if (flashTimerRef.current[rowId]) clearTimeout(flashTimerRef.current[rowId]);
        setFlashByRow((prev) => ({ ...prev, [rowId]: { kind: 'ok' } }));
        flashTimerRef.current[rowId] = setTimeout(() => {
          setFlashByRow((prev) => { const n = { ...prev }; delete n[rowId]; return n; });
        }, 8000);
        // Pull the canonical row back so any backend-side cleaning
        // (e.g. key slug, cascade rename) is reflected locally.
        await reload();
      } catch (e) {
        setFlashByRow((prev) => ({ ...prev, [rowId]: { kind: 'err', msg: e?.response?.data?.detail || 'Save failed' } }));
      }
    }, 900);
  }, [api, reload]);

  const undoRow = useCallback(async (rowId) => {
    const snap = undoSnapshotRef.current[rowId];
    if (!snap) return;
    delete undoSnapshotRef.current[rowId];
    if (flashTimerRef.current[rowId]) clearTimeout(flashTimerRef.current[rowId]);
    setFlashByRow((prev) => { const n = { ...prev }; delete n[rowId]; return n; });
    try {
      await api.put(`/pipe-usages/${rowId}`, {
        label: snap.label || '',
        key: snap.key || '',
        diameter_mm: snap.diameter_mm ?? null,
        notes: snap.notes || '',
        dekite_std_id: snap.dekite_std_id || '',
        dekite_retrofit_id: snap.dekite_retrofit_id || '',
        dekite_lead_id: snap.dekite_lead_id || '',
        btray_linkage_id: snap.btray_linkage_id || '',
      });
      await reload();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Undo failed');
    }
  }, [api, reload]);

  const create = async () => {
    setErr('');
    if (!newRow.label.trim()) { setErr('Label required'); return; }
    setBusy(true);
    try {
      await api.post('/pipe-usages', {
        label: newRow.label.trim(),
        diameter_mm: newRow.diameter_mm === '' ? null : parseInt(newRow.diameter_mm, 10),
        notes: newRow.notes,
      });
      setShowNew(false);
      setNewRow({ label: '', diameter_mm: '', notes: '' });
      await reload();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not create');
    } finally { setBusy(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete pipe usage "${row.label}"?`)) return;
    setErr('');
    try {
      await api.delete(`/pipe-usages/${row._id}`);
      await reload();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not delete');
    }
  };

  // All existing keys — feeds the Key column's datalist combobox.
  const allKeys = rows.map(r => r.key).filter(Boolean);

  return (
    <div data-testid="pipe-usages-editor" className="space-y-3 pb-24">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-slate-800">Pipe Usages</h2>
          <p className="text-xs text-slate-500">Click any cell to edit — changes auto-save with an Undo pill. Renaming a Key cascades to every flashing template that references it.</p>
        </div>
        <button
          type="button"
          data-testid="pipe-usage-add-btn"
          onClick={() => setShowNew(true)}
          className="flex items-center gap-1.5 text-xs font-bold text-white bg-[#2F855A] hover:bg-[#22543D] px-3 py-1.5 rounded-md"
        ><Plus size={13} /> Add usage</button>
      </div>
      {err && <div className="text-xs text-red-700 bg-red-50 ring-1 ring-red-200 rounded px-2 py-1.5">{err}</div>}

      {showNew && (
        <div data-testid="pipe-usage-new-row" className="bg-emerald-50 ring-1 ring-emerald-200 rounded p-3 grid grid-cols-1 md:grid-cols-[1fr_120px_1fr_auto] gap-2 items-end">
          <label className="text-[10px] font-bold uppercase tracking-wider text-emerald-800">
            Label
            <input
              data-testid="pipe-usage-new-label"
              value={newRow.label}
              onChange={(e) => setNewRow({ ...newRow, label: e.target.value })}
              placeholder='e.g. "Breather 80"'
              className="w-full mt-0.5 px-2 py-1.5 border border-emerald-300 rounded text-sm normal-case font-normal"
            />
          </label>
          <label className="text-[10px] font-bold uppercase tracking-wider text-emerald-800">
            Default Ø (mm)
            <input
              data-testid="pipe-usage-new-dia"
              type="number" min={5} max={500} step={1}
              value={newRow.diameter_mm}
              onChange={(e) => setNewRow({ ...newRow, diameter_mm: e.target.value })}
              placeholder="optional"
              className="w-full mt-0.5 px-2 py-1.5 border border-emerald-300 rounded text-sm font-mono-num normal-case font-normal"
            />
          </label>
          <label className="text-[10px] font-bold uppercase tracking-wider text-emerald-800">
            Notes
            <input
              data-testid="pipe-usage-new-notes"
              value={newRow.notes}
              onChange={(e) => setNewRow({ ...newRow, notes: e.target.value })}
              className="w-full mt-0.5 px-2 py-1.5 border border-emerald-300 rounded text-sm normal-case font-normal"
            />
          </label>
          <div className="flex gap-1.5">
            <button
              type="button"
              data-testid="pipe-usage-new-save"
              onClick={create}
              disabled={busy}
              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded text-xs font-bold"
            >{busy ? '…' : 'Save'}</button>
            <button
              type="button"
              onClick={() => { setShowNew(false); setNewRow({ label: '', diameter_mm: '', notes: '' }); setErr(''); }}
              className="px-2 py-1.5 text-xs text-slate-600 underline"
            >Cancel</button>
          </div>
        </div>
      )}

      {/* Shared datalist for the Key combobox below */}
      <datalist id="pipe-usage-key-options">
        {allKeys.map(k => <option key={k} value={k} />)}
      </datalist>

      <div className="bg-white border border-[#E2E2DF] rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Label</th>
              <th className="text-left px-3 py-2">Key</th>
              <th className="text-left px-3 py-2 w-24">Default Ø</th>
              <th className="text-left px-3 py-2 w-24" title="Iter229q — which Annotator pin type offers this usage. Pipe=only Pin.Pipe; Flue=only Pin.Flue; Both=both.">Scope</th>
              <th className="text-left px-3 py-2 min-w-[180px]">Notes</th>
              <th className="text-left px-3 py-2 min-w-[220px]">
                <div className="flex items-center justify-between gap-2">
                  <span>Std Dektite (over-the-top)</span>
                  <a href="/component-rates?component=aquaseal&variant=Std" className="text-[10px] font-bold text-[#D35400] hover:underline whitespace-nowrap normal-case" title="Open the Dektite rate-table editor — manage names, supplier codes, and rates (use ← Back to return)">+ catalogue →</a>
                </div>
              </th>
              <th className="text-left px-3 py-2 min-w-[220px]">
                <div className="flex items-center justify-between gap-2">
                  <span>Retrofit Dektite (cut + clip)</span>
                  <a href="/component-rates?component=aquaseal&variant=Retrofit" className="text-[10px] font-bold text-[#D35400] hover:underline whitespace-nowrap normal-case" title="Open the Dektite rate-table editor — filtered to Retrofit only (use ← Back to return)">+ catalogue →</a>
                </div>
              </th>
              <th className="text-left px-3 py-2 min-w-[200px]">
                <div className="flex items-center justify-between gap-2">
                  <span>Lead-Based (tile roofs)</span>
                  <a href="/component-rates?component=aquaseal&variant=Lead" className="text-[10px] font-bold text-[#D35400] hover:underline whitespace-nowrap normal-case" title="Open the Dektite rate-table editor — filtered to Lead-based only (concrete &amp; clay tile roofs; use ← Back to return)">+ catalogue →</a>
                </div>
              </th>
              <th className="text-left px-3 py-2 min-w-[200px]">Btray flashing linkage</th>
              <th className="text-right px-3 py-2 w-32">Status / Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const fl = flashByRow[row._id];
              const hasSnap = !!undoSnapshotRef.current[row._id];
              return (
                <tr key={row._id} data-testid={`pipe-usage-row-${row.key}`} className="border-t border-slate-100 align-top">
                  <td className="px-3 py-2">
                    <input
                      data-testid={`pipe-usage-cell-label-${row.key}`}
                      value={row.label || ''}
                      onChange={(e) => autoSaveField(row._id, row, 'label', e.target.value)}
                      className="w-full px-2 py-1 border border-transparent hover:border-slate-200 focus:border-[#D35400] rounded text-sm font-semibold outline-none"
                    />
                    {row.is_seed && <span className="ml-1 text-[9px] uppercase tracking-wider bg-amber-100 text-amber-800 rounded px-1.5 py-0.5">seed</span>}
                  </td>
                  <td className="px-3 py-2">
                    <input
                      data-testid={`pipe-usage-cell-key-${row.key}`}
                      list="pipe-usage-key-options"
                      value={row.key || ''}
                      onChange={(e) => autoSaveField(row._id, row, 'key', e.target.value)}
                      className="w-full px-2 py-1 border border-transparent hover:border-slate-200 focus:border-[#D35400] rounded text-[11px] font-mono outline-none"
                      title="Pick from existing keys or type a new one. Renames cascade through flashing templates."
                    />
                  </td>
                  <td className="px-3 py-2 font-mono-num">
                    <input
                      data-testid={`pipe-usage-cell-dia-${row.key}`}
                      type="number" min={5} max={500} step={1}
                      value={row.diameter_mm ?? ''}
                      onChange={(e) => autoSaveField(row._id, row, 'diameter_mm', e.target.value)}
                      placeholder="—"
                      className="w-20 px-2 py-1 border border-transparent hover:border-slate-200 focus:border-[#D35400] rounded text-sm font-mono-num outline-none"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <select
                      data-testid={`pipe-usage-cell-scope-${row.key}`}
                      value={row.pin_type_scope || 'both'}
                      onChange={(e) => autoSaveField(row._id, row, 'pin_type_scope', e.target.value)}
                      className="w-full px-2 py-1 border border-transparent hover:border-slate-200 focus:border-[#D35400] rounded text-xs bg-white outline-none"
                      title="Pipe-only usages appear in Pin.Pipe; Flue-only in Pin.Flue; Both in either."
                    >
                      <option value="pipe">Pipe</option>
                      <option value="flue">Flue</option>
                      <option value="both">Both</option>
                    </select>
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    <input
                      data-testid={`pipe-usage-cell-notes-${row.key}`}
                      value={row.notes || ''}
                      onChange={(e) => autoSaveField(row._id, row, 'notes', e.target.value)}
                      className="w-full px-2 py-1 border border-transparent hover:border-slate-200 focus:border-[#D35400] rounded text-sm outline-none"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <select
                      data-testid={`pipe-usage-cell-dekite-std-${row.key}`}
                      value={row.dekite_std_id || row.dekite_linkage_id || ''}
                      onChange={(e) => autoSaveField(row._id, row, 'dekite_std_id', e.target.value)}
                      className="w-full px-2 py-1 border border-slate-200 hover:border-[#D35400] rounded text-xs bg-white outline-none focus:border-[#D35400]"
                      title="Standard (over-the-top boot) Aquaseal Dektite — fits over a clean hole-cut, the most common case"
                    >
                      <option value="">— no Std Dektite linked —</option>
                      {linkOptions.dekite_std.length === 0 && <option disabled>(no std Dektite types in catalogue)</option>}
                      {linkOptions.dekite_std.map(o => (
                        <option key={o.id} value={o.id}>{o.name}{o.supplier_code ? ` · ${o.supplier_code}` : ''}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <select
                      data-testid={`pipe-usage-cell-dekite-retrofit-${row.key}`}
                      value={row.dekite_retrofit_id || ''}
                      onChange={(e) => autoSaveField(row._id, row, 'dekite_retrofit_id', e.target.value)}
                      className="w-full px-2 py-1 border border-slate-200 hover:border-[#D35400] rounded text-xs bg-white outline-none focus:border-[#D35400]"
                      title="Retrofit Aquaseal Dektite (cut + wrap + clip) for fitting around an obstructed pipe — used when the pipe has bends or fittings wider than the pipe Ø"
                    >
                      <option value="">— no Retrofit Dektite linked —</option>
                      {linkOptions.dekite_retrofit.length === 0 && <option disabled>(no retrofit Dektite types in catalogue)</option>}
                      {linkOptions.dekite_retrofit.map(o => (
                        <option key={o.id} value={o.id}>{o.name}{o.supplier_code ? ` · ${o.supplier_code}` : ''}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <select
                      data-testid={`pipe-usage-cell-dekite-lead-${row.key}`}
                      value={row.dekite_lead_id || ''}
                      onChange={(e) => autoSaveField(row._id, row, 'dekite_lead_id', e.target.value)}
                      className="w-full px-2 py-1 border border-slate-200 hover:border-[#D35400] rounded text-xs bg-white outline-none focus:border-[#D35400]"
                      title="Lead-Based Aquaseal — used on concrete & clay tile roofs (modern and old). Lead sheet is malleable so it conforms to the tile profile around the pipe."
                    >
                      <option value="">— no Lead-Based linked —</option>
                      {(linkOptions.dekite_lead || []).length === 0 && <option disabled>(no Lead-Based aquaseals in catalogue)</option>}
                      {(linkOptions.dekite_lead || []).map(o => (
                        <option key={o.id} value={o.id}>{o.name}{o.supplier_code ? ` · ${o.supplier_code}` : ''}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <select
                      data-testid={`pipe-usage-cell-btray-${row.key}`}
                      value={row.btray_linkage_id || ''}
                      onChange={(e) => autoSaveField(row._id, row, 'btray_linkage_id', e.target.value)}
                      className="w-full px-2 py-1 border border-slate-200 hover:border-[#D35400] rounded text-xs bg-white outline-none focus:border-[#D35400]"
                    >
                      <option value="">— no Btray linked —</option>
                      {linkOptions.btray.length === 0 && <option disabled>(seed a Back Tray flashing template first)</option>}
                      {linkOptions.btray.map(o => (
                        <option key={o.id} value={o.id}>{o.name}{o.category ? ` · ${o.category}` : ''}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      {fl && (
                        <div className="flex items-center gap-1" data-testid={`pipe-usage-flash-${row.key}`}>
                          {fl.kind === 'ok' ? (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 text-[10px] font-semibold">✓ Saved</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700 ring-1 ring-rose-200 text-[10px] font-semibold" title={fl.msg}>⚠ Failed</span>
                          )}
                          {fl.kind === 'ok' && hasSnap && (
                            <button
                              data-testid={`pipe-usage-undo-${row.key}`}
                              onClick={() => undoRow(row._id)}
                              className="px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-300 text-[10px] font-semibold hover:bg-amber-100"
                            >↶ Undo</button>
                          )}
                        </div>
                      )}
                      {!row.is_seed && (
                        <button
                          data-testid={`pipe-usage-delete-${row.key}`}
                          onClick={() => remove(row)}
                          className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                          title="Delete this usage"
                        ><Trash2 size={11} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-sm text-slate-400">No pipe usages configured.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Flashing Categories editor — manages the `flashing_profile` dropdown
// options (Back tray, Apron, Barge, Ridge, Valley, Custom, …). Backed by
// the existing /api/dropdowns/flashing_profile endpoints + a new
// /rename endpoint that cascades the new category name onto existing
// flashing_templates. Deleting a category re-buckets its templates into
// "Custom" so nothing orphans.
// ---------------------------------------------------------------------------
const SEED_FLASHING_CATEGORIES = ['Apron', 'Barge', 'Box Gutter', 'Change Of Pitch', 'Skirt', 'Valley', 'Parapet', 'Gutter Flashing', 'Back Tray', 'Custom'];

function FlashingCategoriesEditor({ api }) {
  const [options, setOptions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [newVal, setNewVal] = useState('');
  const [editIdx, setEditIdx] = useState(-1);
  const [editVal, setEditVal] = useState('');
  // Per-category template list + selected default. `templatesByCat`
  // is fetched once on mount; `defaults[category]` holds the picked
  // template id. Auto-save fires on every <select> change.
  const [templatesByCat, setTemplatesByCat] = useState({});
  // Flat id → full template doc lookup so the Preview column can
  // render the FlashingProfileSVG for the picked default without
  // re-scanning the category bucket.
  const [templatesById, setTemplatesById] = useState({});
  const [defaults, setDefaults] = useState({});
  // Hover preview (which category's preview cell is being hovered)
  // + click-to-view modal target template.
  const [hoverCat, setHoverCat] = useState('');
  const [viewerTplId, setViewerTplId] = useState('');
  // Per-row auto-save flash + undo snapshot (same pattern as the
  // Pipe Usages editor).
  const [flashByCat, setFlashByCat] = useState({});
  const undoSnapshotRef = useRef({});
  const flashTimerRef = useRef({});

  const reload = useCallback(async () => {
    try {
      const [r, t, d] = await Promise.all([
        api.get('/dropdowns/flashing_profile'),
        api.get('/flashing-templates'),
        api.get('/flashing-categories/defaults').catch(() => ({ data: {} })),
      ]);
      const cats = Array.isArray(r.data) ? r.data : [];
      setOptions(cats);
      // Bucket templates by category for the per-row dropdown + build
      // the flat id-lookup map.
      const bucket = {};
      const byId = {};
      (Array.isArray(t.data) ? t.data : []).forEach((tpl) => {
        const c = tpl.category || 'Custom';
        if (!bucket[c]) bucket[c] = [];
        bucket[c].push(tpl);
        byId[tpl._id] = tpl;
      });
      setTemplatesByCat(bucket);
      setTemplatesById(byId);
      setDefaults(d.data || {});
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load');
    }
  }, [api]);

  useEffect(() => { reload(); }, [reload]);

  // Esc closes the full-view template modal.
  useEffect(() => {
    if (!viewerTplId) return;
    const onKey = (e) => { if (e.key === 'Escape') setViewerTplId(''); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewerTplId]);

  // Auto-save the default template for a category. Snapshots the
  // previous value on first change so Undo can revert.
  const setCategoryDefault = async (category, templateId) => {
    if (!(category in undoSnapshotRef.current)) {
      undoSnapshotRef.current[category] = defaults[category] || '';
    }
    setDefaults((prev) => ({ ...prev, [category]: templateId }));
    try {
      await api.put(
        `/flashing-categories/${encodeURIComponent(category)}/default-template`,
        { template_id: templateId || '' },
      );
      if (flashTimerRef.current[category]) clearTimeout(flashTimerRef.current[category]);
      setFlashByCat((prev) => ({ ...prev, [category]: { kind: 'ok' } }));
      flashTimerRef.current[category] = setTimeout(() => {
        setFlashByCat((prev) => { const n = { ...prev }; delete n[category]; return n; });
      }, 8000);
    } catch (e) {
      setFlashByCat((prev) => ({ ...prev, [category]: { kind: 'err', msg: e?.response?.data?.detail || 'Save failed' } }));
    }
  };

  const undoCategory = async (category) => {
    if (!(category in undoSnapshotRef.current)) return;
    const prev = undoSnapshotRef.current[category];
    delete undoSnapshotRef.current[category];
    if (flashTimerRef.current[category]) clearTimeout(flashTimerRef.current[category]);
    setFlashByCat((prevFl) => { const n = { ...prevFl }; delete n[category]; return n; });
    setDefaults((d) => ({ ...d, [category]: prev }));
    try {
      await api.put(
        `/flashing-categories/${encodeURIComponent(category)}/default-template`,
        { template_id: prev || '' },
      );
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Undo failed');
    }
  };

  const addOption = async () => {
    setErr('');
    const v = newVal.trim();
    if (!v) return;
    if (options.some(o => o.toLowerCase() === v.toLowerCase())) {
      setErr('Already exists');
      return;
    }
    setBusy(true);
    try {
      await api.post('/dropdowns/flashing_profile/add', { value: v });
      setNewVal('');
      await reload();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not add');
    } finally { setBusy(false); }
  };

  const startEdit = (idx, v) => { setEditIdx(idx); setEditVal(v); setErr(''); };

  const saveRename = async () => {
    setErr('');
    const fromVal = options[editIdx];
    const toVal = editVal.trim();
    if (!toVal || toVal === fromVal) { setEditIdx(-1); return; }
    setBusy(true);
    try {
      await api.post('/dropdowns/flashing_profile/rename', {
        old_value: fromVal, new_value: toVal,
      });
      setEditIdx(-1);
      await reload();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not rename');
    } finally { setBusy(false); }
  };

  const removeOption = async (v) => {
    if (!window.confirm(`Delete category "${v}"? Existing templates in this category will be moved to "Custom".`)) return;
    setErr('');
    setBusy(true);
    try {
      await api.post('/dropdowns/flashing_profile/remove', { value: v });
      await reload();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not delete');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="flashing-categories-editor" className="space-y-3 pb-24">
      <div>
        <h2 className="text-base font-bold text-slate-800">Flashing Categories</h2>
        <p className="text-xs text-slate-500">Drives the Category picker on the Flashing Template editor. Renames cascade to existing templates; deletes re-bucket those templates into <span className="font-mono">Custom</span>.</p>
      </div>
      {err && <div className="text-xs text-red-700 bg-red-50 ring-1 ring-red-200 rounded px-2 py-1.5">{err}</div>}

      <div className="bg-emerald-50 ring-1 ring-emerald-200 rounded p-3 flex items-end gap-2">
        <label className="flex-1 text-[10px] font-bold uppercase tracking-wider text-emerald-800">
          Add new category
          <input
            data-testid="flashing-cat-new-input"
            value={newVal}
            onChange={(e) => setNewVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addOption(); }}
            placeholder='e.g. "Penetration Skirt"'
            className="w-full mt-0.5 px-2 py-1.5 border border-emerald-300 rounded text-sm normal-case font-normal"
          />
        </label>
        <button
          type="button"
          data-testid="flashing-cat-add-btn"
          onClick={addOption}
          disabled={busy || !newVal.trim()}
          className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded text-xs font-bold inline-flex items-center gap-1"
        ><Plus size={13} /> Add</button>
      </div>

      <div className="bg-white border border-[#E2E2DF] rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Category</th>
              <th className="text-left px-3 py-2 w-24">Type</th>
              <th className="text-left px-3 py-2 w-[180px]">Flashing template (default)</th>
              <th className="text-left px-3 py-2 w-[120px]">Preview</th>
              <th className="text-right px-3 py-2 w-40">Actions</th>
            </tr>
          </thead>
          <tbody>
            {options.map((opt, idx) => {
              const isSeed = SEED_FLASHING_CATEGORIES.includes(opt);
              const isEdit = editIdx === idx;
              const catTemplates = templatesByCat[opt] || [];
              const fl = flashByCat[opt];
              const hasSnap = opt in undoSnapshotRef.current;
              const defaultTplId = defaults[opt] || '';
              const defaultTpl = defaultTplId ? templatesById[defaultTplId] : null;
              return (
                <tr key={opt} data-testid={`flashing-cat-row-${opt.replace(/\s+/g, '_')}`} className="border-t border-slate-100">
                  <td className="px-3 py-2">
                    {isEdit ? (
                      <input
                        data-testid={`flashing-cat-edit-input-${idx}`}
                        value={editVal}
                        onChange={(e) => setEditVal(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveRename();
                          if (e.key === 'Escape') setEditIdx(-1);
                        }}
                        className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
                        autoFocus
                      />
                    ) : (
                      <span className="font-semibold text-slate-800">{opt}</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {isSeed ? (
                      <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 rounded px-1.5 py-0.5">seed</span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">custom</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <select
                        data-testid={`flashing-cat-template-${opt.replace(/\s+/g, '_')}`}
                        value={defaultTplId}
                        onChange={(e) => setCategoryDefault(opt, e.target.value)}
                        className="flex-1 min-w-0 px-2 py-1 border border-slate-200 hover:border-[#D35400] focus:border-[#D35400] rounded text-xs bg-white outline-none truncate"
                      >
                        <option value="">— no default —</option>
                        {catTemplates.length === 0 && <option disabled>(no templates in this category yet)</option>}
                        {catTemplates.map((tpl) => (
                          <option key={tpl._id} value={tpl._id}>{tpl.name}</option>
                        ))}
                      </select>
                      <span className="text-[9px] text-slate-400 whitespace-nowrap">{catTemplates.length}</span>
                      {fl && (
                        <span className="flex items-center gap-1" data-testid={`flashing-cat-flash-${opt.replace(/\s+/g, '_')}`}>
                          {fl.kind === 'ok' ? (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 text-[10px] font-semibold">✓</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700 ring-1 ring-rose-200 text-[10px] font-semibold" title={fl.msg}>⚠</span>
                          )}
                          {fl.kind === 'ok' && hasSnap && (
                            <button
                              data-testid={`flashing-cat-undo-${opt.replace(/\s+/g, '_')}`}
                              onClick={() => undoCategory(opt)}
                              className="px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-300 text-[10px] font-semibold hover:bg-amber-100"
                              title="Undo last change"
                            >↶</button>
                          )}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {defaultTpl && Array.isArray(defaultTpl.folds) && defaultTpl.folds.length > 0 ? (
                      <div
                        className="relative inline-block cursor-zoom-in"
                        onMouseEnter={() => setHoverCat(opt)}
                        onMouseLeave={() => setHoverCat('')}
                        onClick={() => setViewerTplId(defaultTpl._id)}
                        data-testid={`flashing-cat-preview-${opt.replace(/\s+/g, '_')}`}
                        title="Hover to enlarge · click to open full view"
                      >
                        <div className="ring-1 ring-slate-200 rounded bg-slate-50 p-1">
                          <FlashingProfileSVG
                            folds={defaultTpl.folds}
                            startAngle={defaultTpl.start_angle_deg}
                            edges={defaultTpl.edges}
                            edgeConfigs={defaultTpl.edge_configs}
                            width={100}
                            height={50}
                            showLabels={false}
                          />
                        </div>
                        {/* Hover-zoom popover. Absolutely positioned so it
                            doesn't reflow the row; rendered in front of the
                            next row via z-20. */}
                        {hoverCat === opt && (
                          <div
                            className="absolute left-full top-0 ml-2 z-20 bg-white ring-1 ring-slate-300 shadow-xl rounded p-3 pointer-events-none"
                            data-testid={`flashing-cat-preview-zoom-${opt.replace(/\s+/g, '_')}`}
                          >
                            <FlashingProfileSVG
                              folds={defaultTpl.folds}
                              startAngle={defaultTpl.start_angle_deg}
                              edges={defaultTpl.edges}
                              edgeConfigs={defaultTpl.edge_configs}
                              width={360}
                              height={180}
                              showLabels={true}
                              labelsLayout="right-column"
                            />
                            <div className="mt-2 text-[11px] text-slate-700 font-semibold">{defaultTpl.name}</div>
                            <div className="text-[10px] text-slate-500 italic">click to open full view</div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-[10px] text-slate-300 italic">no preview</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {isEdit ? (
                      <div className="flex gap-1 justify-end">
                        <button
                          data-testid={`flashing-cat-save-${idx}`}
                          onClick={saveRename}
                          disabled={busy}
                          className="px-2 py-1 bg-[#D35400] hover:bg-[#A04000] disabled:opacity-50 text-white rounded text-xs font-bold"
                        >Save</button>
                        <button
                          onClick={() => setEditIdx(-1)}
                          className="px-2 py-1 text-xs text-slate-500 underline"
                        >Cancel</button>
                      </div>
                    ) : (
                      <div className="flex gap-1 justify-end">
                        <button
                          data-testid={`flashing-cat-edit-${opt.replace(/\s+/g, '_')}`}
                          onClick={() => startEdit(idx, opt)}
                          className="px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 rounded"
                        >Rename</button>
                        {!isSeed && (
                          <button
                            data-testid={`flashing-cat-delete-${opt.replace(/\s+/g, '_')}`}
                            onClick={() => removeOption(opt)}
                            className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded"
                          ><Trash2 size={11} /></button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {options.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-sm text-slate-400">No categories configured yet. Add one above.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Full-view modal — read-only render of the picked default
          template (large canvas + every fold label visible). Closes on
          backdrop click or Esc. */}
      {viewerTplId && templatesById[viewerTplId] && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          data-testid="flashing-cat-viewer-modal"
          onClick={() => setViewerTplId('')}
        >
          <div
            className="bg-white rounded-md shadow-2xl ring-1 ring-slate-300 w-full max-w-3xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 px-5 py-4 border-b border-slate-200">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase font-bold tracking-wide text-slate-500">
                  Flashing template · view-only
                </div>
                <h3 className="text-lg font-bold text-[#0F172A]">{templatesById[viewerTplId].name}</h3>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  Category: <span className="font-semibold text-slate-700">{templatesById[viewerTplId].category || '—'}</span>
                  {templatesById[viewerTplId].flashing_profile && (
                    <> · Profile: <span className="font-semibold text-slate-700">{templatesById[viewerTplId].flashing_profile}</span></>
                  )}
                </div>
              </div>
              <button
                onClick={() => setViewerTplId('')}
                data-testid="flashing-cat-viewer-close"
                className="text-slate-400 hover:text-slate-700"
                aria-label="Close"
              ><X size={18} /></button>
            </div>
            <div className="p-5">
              <div className="ring-1 ring-slate-200 rounded bg-slate-50 p-4 flex items-center justify-center">
                <FlashingProfileSVG
                  folds={templatesById[viewerTplId].folds || []}
                  startAngle={templatesById[viewerTplId].start_angle_deg}
                  edges={templatesById[viewerTplId].edges}
                  edgeConfigs={templatesById[viewerTplId].edge_configs}
                  width={620}
                  height={300}
                  showLabels={true}
                  labelsLayout="perpendicular"
                />
              </div>
              {/* Fold list table */}
              <div className="mt-4 text-xs">
                <div className="font-semibold text-slate-600 mb-1">Folds ({(templatesById[viewerTplId].folds || []).length})</div>
                <table className="w-full ring-1 ring-slate-200 rounded overflow-hidden">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="text-left px-2 py-1.5">#</th>
                      <th className="text-left px-2 py-1.5">Label</th>
                      <th className="text-right px-2 py-1.5">Length (mm)</th>
                      <th className="text-right px-2 py-1.5">Bend (°)</th>
                      <th className="text-left px-2 py-1.5">Bend dir</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(templatesById[viewerTplId].folds || []).map((f, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-2 py-1.5 font-mono-num text-slate-500">{i + 1}</td>
                        <td className="px-2 py-1.5 text-slate-700">{f.label || `Fold ${i + 1}`}</td>
                        <td className="px-2 py-1.5 text-right font-mono-num">{f.length_mm}</td>
                        <td className="px-2 py-1.5 text-right font-mono-num">{f.bend_deg ?? '—'}</td>
                        <td className="px-2 py-1.5 text-slate-600">{f.bend_dir || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────
// Stripping Rates editor — admin override of per-m² stripping labour
// + waste m³ + scrap recycling value for each re-roof type.
// ───────────────────────────────────────────────────────────────────
function StrippingRatesEditor({ api }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState('');

  const reload = useCallback(async () => {
    try {
      const r = await api.get('/reroof-stripping-rates');
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load');
    }
  }, [api]);
  useEffect(() => { reload(); }, [reload]);

  const saveField = async (row, patch) => {
    setBusy(row._id);
    try {
      const r = await api.put(`/reroof-stripping-rates/${row._id}`, patch);
      setRows((prev) => prev.map((x) => x._id === row._id ? r.data : x));
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(null); }
  };

  return (
    <div data-testid="stripping-rates-editor" className="space-y-3 pb-24">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-bold text-slate-800">Reroof Stripping Rates</h2>
          <p className="text-xs text-slate-500">Per-m² strip labour, waste m³, and scrap recycling income for each re-roof type. Concrete tile is non-recyclable (scrap = $0 → dump bins kick in). Tin tile + Iron get a scrap credit reducing the total.</p>
        </div>
        {/* Sync the strip $/m² column from the legacy MiscType table
            (component_rate_tables · component='misc' · STRIP ROOF *).
            Operator can still override per-row after a sync — the next
            sync will only flag rows whose values actually drifted. */}
        <button
          type="button"
          data-testid="stripping-rates-sync-from-misc-btn"
          onClick={async () => {
            setErr('');
            setBusy('sync');
            try {
              const r = await api.post('/reroof-stripping-rates/sync-from-misc');
              const u = (r.data?.updated || []).length;
              const s = (r.data?.skipped || []).length;
              await reload();
              setErr(`Synced from MiscType: ${u} row${u === 1 ? '' : 's'} updated · ${s} skipped (already in sync / no mapping)`);
            } catch (e) {
              setErr(e?.response?.data?.detail || 'Sync failed');
            } finally { setBusy(null); }
          }}
          disabled={busy === 'sync'}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded ring-1 ring-orange-300 bg-orange-50 hover:bg-orange-100 text-orange-800 disabled:opacity-50"
          title="Pull the strip $/m² column from the MiscType rate table (STRIP ROOF Conc/m2, STRIP ROOF Iron/m2, STRIP ROOF TinTile/m2)"
        >
          {busy === 'sync' ? 'Syncing…' : 'Sync from MiscType'}
        </button>
      </div>
      {err && (
        <div
          className={`text-xs rounded px-2 py-1.5 ring-1 ${
            err.toLowerCase().includes('synced')
              ? 'text-emerald-800 bg-emerald-50 ring-emerald-200'
              : 'text-red-700 bg-red-50 ring-red-200'
          }`}
          data-testid="stripping-rates-flash"
        >
          {err}
        </div>
      )}
      <div className="bg-white border border-[#E2E2DF] rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Re-roof type</th>
              <th className="text-left px-3 py-2">Old material</th>
              <th className="text-right px-3 py-2 w-28">Strip $/m²</th>
              <th className="text-right px-3 py-2 w-32">Medium thickness (mm)</th>
              <th className="text-right px-3 py-2 w-36">Waste m³/m² <span className="text-[9px] font-normal normal-case text-slate-400">(auto · editable)</span></th>
              <th className="text-right px-3 py-2 w-28">Scrap $/m²</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <StrippingRateRow key={row._id} row={row} busy={busy === row._id} onSave={(patch) => saveField(row, patch)} />
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400 text-sm">Loading defaults…</td></tr>
            )}
          </tbody>
        </table>
        <div className="px-3 py-2 text-[11px] text-slate-500 bg-slate-50 border-t border-slate-200">
          <span className="font-semibold text-slate-700">Volume reckoner:</span>{' '}
          Estimated dump volume = <code className="font-mono">Medium thickness (m) × Roof m²</code>.
          Concrete tile ≈ 25 mm = 0.025 m³/m² → a 200 m² roof generates ≈ 5 m³ of broken-tile waste.
          The waste column auto-fills from thickness; bump it manually if you want a packing factor (broken tiles bulk by ~1.6× in a skip).
        </div>
      </div>
    </div>
  );
}

function StrippingRateRow({ row, busy, onSave }) {
  const [form, setForm] = useState(row);
  useEffect(() => { setForm(row); }, [row._id, row.updated_at]);
  const commitNum = (key) => {
    const v = form[key] === '' ? 0 : Number(form[key]);
    if (v !== row[key]) onSave({ [key]: v });
  };
  // When the operator changes Medium thickness (mm), live-mirror it
  // onto the Waste m³/m² column so the dump volume preview stays in
  // sync. 25 mm → 0.025 m³/m². Operator can still override the waste
  // value manually if they want to apply a packing/bulking factor.
  const commitThickness = () => {
    const v = form.medium_thickness_mm === '' ? 0 : Number(form.medium_thickness_mm);
    const derivedWaste = +(v / 1000).toFixed(3);
    const patch = {};
    if (v !== row.medium_thickness_mm) patch.medium_thickness_mm = v;
    // Only push the derived waste if it differs from what's stored —
    // avoids stomping an operator's manual override on second edit.
    if (derivedWaste !== Number(row.waste_m3_per_m2)) {
      patch.waste_m3_per_m2 = derivedWaste;
      setForm((f) => ({ ...f, waste_m3_per_m2: derivedWaste }));
    }
    if (Object.keys(patch).length > 0) onSave(patch);
  };
  const slug = (row.reroof_type || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
  // Live volume preview at the canonical 200 m² benchmark roof — gives
  // the operator an instant sanity check ("yep, 5 m³ skip needed").
  const sampleVol = +((Number(form.waste_m3_per_m2) || 0) * 200).toFixed(2);
  return (
    <tr data-testid={`stripping-rate-row-${slug}`} className="border-t border-slate-100">
      <td className="px-3 py-2 font-semibold text-slate-800">{row.reroof_type}</td>
      <td className="px-3 py-2 text-slate-600">{row.old_material || <span className="text-slate-300">—</span>}</td>
      <td className="px-3 py-2 text-right">
        <input
          data-testid={`stripping-rate-${slug}-strip`}
          type="number" step={0.5}
          value={form.strip_rate_per_m2 ?? 0}
          onChange={(e) => setForm({ ...form, strip_rate_per_m2: e.target.value })}
          onBlur={() => commitNum('strip_rate_per_m2')}
          className="w-24 px-2 py-1 border border-slate-200 rounded text-sm font-mono-num text-right"
        />
      </td>
      <td className="px-3 py-2 text-right">
        <input
          data-testid={`stripping-rate-${slug}-thickness`}
          type="number" step={0.5} min={0}
          value={form.medium_thickness_mm ?? 0}
          onChange={(e) => setForm({ ...form, medium_thickness_mm: e.target.value })}
          onBlur={commitThickness}
          className="w-24 px-2 py-1 border border-slate-200 rounded text-sm font-mono-num text-right"
          title="e.g. concrete tile ≈ 25, tin tile ≈ 5, iron sheet ≈ 1 mm. Updating this auto-fills Waste m³/m²."
        />
      </td>
      <td className="px-3 py-2 text-right">
        <input
          data-testid={`stripping-rate-${slug}-waste`}
          type="number" step={0.001}
          value={form.waste_m3_per_m2 ?? 0}
          onChange={(e) => setForm({ ...form, waste_m3_per_m2: e.target.value })}
          onBlur={() => commitNum('waste_m3_per_m2')}
          className="w-24 px-2 py-1 border border-slate-200 rounded text-sm font-mono-num text-right"
        />
        <div className="text-[10px] text-slate-400 mt-0.5" data-testid={`stripping-rate-${slug}-vol-preview`}>
          @ 200 m² = <span className="font-mono-num">{sampleVol}</span> m³
        </div>
      </td>
      <td className="px-3 py-2 text-right">
        <input
          data-testid={`stripping-rate-${slug}-scrap`}
          type="number" step={0.5}
          value={form.scrap_value_per_m2 ?? 0}
          onChange={(e) => setForm({ ...form, scrap_value_per_m2: e.target.value })}
          onBlur={() => commitNum('scrap_value_per_m2')}
          className="w-24 px-2 py-1 border border-slate-200 rounded text-sm font-mono-num text-right"
        />
        {busy ? <Loader2Icon /> : null}
      </td>
    </tr>
  );
}
function Loader2Icon() { return <span className="ml-1 inline-block w-3 h-3 border-2 border-slate-300 border-t-emerald-600 rounded-full animate-spin" />; }


// ───────────────────────────────────────────────────────────────────
// Skip Bin Suppliers editor — supplier directory with bin sizes +
// per-bin pricing + delivery lead days. Used by the Re-roof Builder's
// dump-bin auto-plan when reroof_type = Concrete Tile.
// ───────────────────────────────────────────────────────────────────
function SkipBinSuppliersEditor({ api }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [newRow, setNewRow] = useState({ name: '', contact_name: '', contact_phone: '', contact_email: '', delivery_lead_days: 2, notes: '' });

  const reload = useCallback(async () => {
    try {
      const r = await api.get('/skip-bin-suppliers');
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load');
    }
  }, [api]);
  useEffect(() => { reload(); }, [reload]);

  const create = async () => {
    if (!newRow.name.trim()) { setErr('Supplier name required'); return; }
    setBusy('new');
    try {
      await api.post('/skip-bin-suppliers', {
        ...newRow,
        bins: [
          { size_m3: 4.5, rate_each: 250, label: '4.5 m³ midi' },
          { size_m3: 6.0, rate_each: 320, label: '6 m³ standard' },
          { size_m3: 9.0, rate_each: 420, label: '9 m³ maxi' },
        ],
      });
      setNewRow({ name: '', contact_name: '', contact_phone: '', contact_email: '', delivery_lead_days: 2, notes: '' });
      setShowNew(false);
      await reload();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not create');
    } finally { setBusy(null); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete supplier "${row.name}"?`)) return;
    try {
      await api.delete(`/skip-bin-suppliers/${row._id}`);
      await reload();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div data-testid="skip-bin-suppliers-editor" className="space-y-3 pb-24">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-slate-800">Skip Bin Suppliers</h2>
          <p className="text-xs text-slate-500">Supplier directory + per-bin pricing. The Re-roof Builder's dump plan picks the cheapest mix that holds the waste volume.</p>
        </div>
        <button type="button" data-testid="bin-supplier-add-btn" onClick={() => setShowNew(true)} className="px-3 py-1.5 bg-[#2F855A] hover:bg-[#22543D] text-white rounded text-xs font-bold inline-flex items-center gap-1">
          <Plus size={13} /> Add supplier
        </button>
      </div>
      {err && <div className="text-xs text-red-700 bg-red-50 ring-1 ring-red-200 rounded px-2 py-1.5">{err}</div>}
      {showNew && (
        <div className="bg-emerald-50 ring-1 ring-emerald-200 rounded p-3 grid grid-cols-1 md:grid-cols-3 gap-2">
          <input data-testid="bin-supplier-new-name" placeholder="Supplier name" value={newRow.name} onChange={(e) => setNewRow({ ...newRow, name: e.target.value })} className="px-2 py-1.5 border border-emerald-300 rounded text-sm" />
          <input data-testid="bin-supplier-new-phone" placeholder="Phone" value={newRow.contact_phone} onChange={(e) => setNewRow({ ...newRow, contact_phone: e.target.value })} className="px-2 py-1.5 border border-emerald-300 rounded text-sm" />
          <input data-testid="bin-supplier-new-email" placeholder="Email" value={newRow.contact_email} onChange={(e) => setNewRow({ ...newRow, contact_email: e.target.value })} className="px-2 py-1.5 border border-emerald-300 rounded text-sm" />
          <div className="md:col-span-3 flex gap-2">
            <button type="button" data-testid="bin-supplier-new-save" onClick={create} disabled={busy === 'new'} className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded text-xs font-bold">Save</button>
            <button type="button" onClick={() => setShowNew(false)} className="px-3 py-1.5 text-xs underline text-slate-600">Cancel</button>
            <span className="text-[10px] text-emerald-700 italic ml-auto self-center">Default bins (4.5 / 6 / 9 m³ @ $250/$320/$420) auto-seeded — edit after save.</span>
          </div>
        </div>
      )}
      <div className="space-y-2">
        {rows.map((row) => (
          <SkipBinSupplierRow key={row._id} api={api} row={row} onReload={reload} onRemove={() => remove(row)} />
        ))}
      </div>
    </div>
  );
}

function SkipBinSupplierRow({ api, row, onReload, onRemove }) {
  const [form, setForm] = useState(row);
  const [bins, setBins] = useState(row.bins || []);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(row); setBins(row.bins || []); }, [row._id, row.updated_at]);
  const save = async (patch) => {
    setBusy(true);
    try {
      const r = await api.put(`/skip-bin-suppliers/${row._id}`, patch);
      setForm(r.data); setBins(r.data.bins || []);
      onReload();
    } finally { setBusy(false); }
  };
  const slug = (row.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
  return (
    <div data-testid={`bin-supplier-row-${slug}`} className="bg-white rounded ring-1 ring-slate-200 p-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <input
          data-testid={`bin-supplier-${slug}-name`}
          value={form.name || ''}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          onBlur={() => { if (form.name !== row.name) save({ name: form.name }); }}
          className="flex-1 px-2 py-1 border border-slate-200 rounded text-sm font-semibold"
        />
        {row.is_seed && <span className="text-[9px] uppercase tracking-wider bg-amber-100 text-amber-800 rounded px-1.5 py-0.5">seed</span>}
        <input
          data-testid={`bin-supplier-${slug}-phone`}
          value={form.contact_phone || ''} placeholder="Phone"
          onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
          onBlur={() => { if (form.contact_phone !== row.contact_phone) save({ contact_phone: form.contact_phone }); }}
          className="w-40 px-2 py-1 border border-slate-200 rounded text-xs"
        />
        <input
          data-testid={`bin-supplier-${slug}-email`}
          value={form.contact_email || ''} placeholder="Email"
          onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
          onBlur={() => { if (form.contact_email !== row.contact_email) save({ contact_email: form.contact_email }); }}
          className="w-56 px-2 py-1 border border-slate-200 rounded text-xs"
        />
        <input
          data-testid={`bin-supplier-${slug}-lead`}
          type="number" min={0} max={30}
          value={form.delivery_lead_days ?? 0} placeholder="Lead days"
          onChange={(e) => setForm({ ...form, delivery_lead_days: e.target.value === '' ? 0 : Number(e.target.value) })}
          onBlur={() => { if (Number(form.delivery_lead_days) !== row.delivery_lead_days) save({ delivery_lead_days: Number(form.delivery_lead_days) }); }}
          className="w-20 px-2 py-1 border border-slate-200 rounded text-xs font-mono-num text-right"
          title="Delivery lead days"
        />
        {!row.is_seed && (
          <button type="button" data-testid={`bin-supplier-${slug}-delete`} onClick={onRemove} className="text-xs text-red-600 hover:bg-red-50 rounded px-2 py-1"><Trash2 size={11} /></button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {bins.map((b, i) => (
          <div key={i} className="flex items-center gap-1 rounded ring-1 ring-slate-200 bg-slate-50 px-2 py-1">
            <input
              value={b.label || ''} placeholder="Label"
              onChange={(e) => setBins(bins.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}
              onBlur={() => save({ bins })}
              className="flex-1 text-xs px-1.5 py-0.5 border border-slate-200 rounded"
            />
            <input
              type="number" step={0.1} value={b.size_m3 ?? 0}
              onChange={(e) => setBins(bins.map((x, j) => j === i ? { ...x, size_m3: Number(e.target.value) } : x))}
              onBlur={() => save({ bins })}
              className="w-16 text-xs font-mono-num px-1.5 py-0.5 border border-slate-200 rounded text-right"
              title="Capacity m³"
            />
            <span className="text-[10px] text-slate-500">m³</span>
            <input
              type="number" step={10} value={b.rate_each ?? 0}
              onChange={(e) => setBins(bins.map((x, j) => j === i ? { ...x, rate_each: Number(e.target.value) } : x))}
              onBlur={() => save({ bins })}
              className="w-20 text-xs font-mono-num px-1.5 py-0.5 border border-slate-200 rounded text-right"
              title="Rate each"
            />
            <span className="text-[10px] text-slate-500">$</span>
            <button type="button" onClick={() => { const next = bins.filter((_, j) => j !== i); setBins(next); save({ bins: next }); }} className="text-[10px] text-red-500 hover:bg-red-50 rounded px-1">×</button>
          </div>
        ))}
        <button type="button" onClick={() => { const next = [...bins, { size_m3: 4.5, rate_each: 250, label: 'New bin' }]; setBins(next); save({ bins: next }); }} className="text-xs text-emerald-700 hover:bg-emerald-50 rounded px-2 py-1 ring-1 ring-emerald-200 inline-flex items-center gap-1 self-start">
          <Plus size={11} /> Add bin size
        </button>
      </div>
      {busy && <div className="text-[10px] text-slate-400 italic">Saving…</div>}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Cultural Phrases editor — per-culture key/label/language + greetings.
// Seeded list is editable; admins can extend with new cultures over time.
// ---------------------------------------------------------------------------
function CulturalPhrasesEditor({ api }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState('');
  const [newRow, setNewRow] = React.useState({ key: '', label: '', language: '', locale: '', greeting: '', thank_you: '', farewell: '' });

  const reload = React.useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/cultural-phrases'); setRows(Array.isArray(r.data) ? r.data : []); }
    catch (e) { setErr(e?.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  }, [api]);
  React.useEffect(() => { reload(); }, [reload]);

  const update = async (id, patch) => {
    try { await api.put(`/cultural-phrases/${id}`, patch); await reload(); }
    catch (e) { setErr(e?.response?.data?.detail || 'Save failed'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Remove this culture? The phrase row will be deleted; clients still flagged with this key will fall back to no greeting.')) return;
    try { await api.delete(`/cultural-phrases/${id}`); await reload(); }
    catch (e) { setErr(e?.response?.data?.detail || 'Delete failed'); }
  };
  const create = async () => {
    if (!newRow.key.trim() || !newRow.label.trim()) { setErr('key and label are required'); return; }
    try {
      await api.post('/cultural-phrases', newRow);
      setNewRow({ key: '', label: '', language: '', locale: '', greeting: '', thank_you: '', farewell: '' });
      await reload();
    } catch (e) { setErr(e?.response?.data?.detail || 'Add failed'); }
  };

  return (
    <div data-testid="cultural-phrases-editor" className="space-y-3 pb-24">
      <div>
        <h2 className="text-base font-bold text-slate-800">Cultural Phrases</h2>
        <p className="text-xs text-slate-500">Per-culture greeting · thank-you · farewell. Drives the Cultural Toolkit card on the appraisal page when a client's cultural_identity matches a row's `key`.</p>
      </div>
      {err && <div className="text-xs text-red-700 bg-red-50 ring-1 ring-red-200 rounded px-2 py-1.5">{err}</div>}
      {loading ? <p className="text-xs text-slate-400">Loading…</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="cultural-phrases-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-2 py-1.5">Key</th>
                <th className="text-left px-2 py-1.5">Label</th>
                <th className="text-left px-2 py-1.5">Language</th>
                <th className="text-left px-2 py-1.5">Greeting</th>
                <th className="text-left px-2 py-1.5">Thank you</th>
                <th className="text-left px-2 py-1.5">Farewell</th>
                <th className="px-2 py-1.5 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r._id} className="border-b border-slate-100 hover:bg-amber-50/30" data-testid={`cultural-phrase-row-${r.key}`}>
                  <td className="px-2 py-1.5 font-mono text-[11px] text-slate-500">{r.key}{r.is_seed && <span className="ml-1 text-[10px] text-amber-600">(seed)</span>}</td>
                  <td className="px-2 py-1"><Inline value={r.label} onBlur={(v) => update(r._id, { label: v })} /></td>
                  <td className="px-2 py-1"><Inline value={r.language || ''} onBlur={(v) => update(r._id, { language: v })} /></td>
                  <td className="px-2 py-1"><Inline value={r.greeting || ''} onBlur={(v) => update(r._id, { greeting: v })} testid={`phrase-${r.key}-greeting`} /></td>
                  <td className="px-2 py-1"><Inline value={r.thank_you || ''} onBlur={(v) => update(r._id, { thank_you: v })} testid={`phrase-${r.key}-thanks`} /></td>
                  <td className="px-2 py-1"><Inline value={r.farewell || ''} onBlur={(v) => update(r._id, { farewell: v })} testid={`phrase-${r.key}-farewell`} /></td>
                  <td className="px-2 py-1.5 text-right">
                    <button onClick={() => remove(r._id)} className="text-rose-500 hover:text-rose-700" data-testid={`phrase-${r.key}-delete`} title="Remove culture"><Trash2 size={12} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="border-t border-slate-200 pt-3 mt-3" data-testid="cultural-phrases-new-row">
        <h3 className="text-xs font-bold text-slate-600 mb-2">Add a culture</h3>
        <div className="grid grid-cols-2 md:grid-cols-7 gap-2 items-end">
          <label className="text-[10px] uppercase tracking-wide text-slate-500 col-span-1"><span>Key*</span><input data-testid="new-phrase-key" className="mt-1 w-full px-2 py-1 border border-slate-300 rounded text-xs font-mono" value={newRow.key} onChange={(e) => setNewRow({ ...newRow, key: e.target.value })} placeholder="e.g. Italian" /></label>
          <label className="text-[10px] uppercase tracking-wide text-slate-500 col-span-1"><span>Label*</span><input data-testid="new-phrase-label" className="mt-1 w-full px-2 py-1 border border-slate-300 rounded text-xs" value={newRow.label} onChange={(e) => setNewRow({ ...newRow, label: e.target.value })} placeholder="Italian" /></label>
          <label className="text-[10px] uppercase tracking-wide text-slate-500 col-span-1"><span>Language</span><input className="mt-1 w-full px-2 py-1 border border-slate-300 rounded text-xs" value={newRow.language} onChange={(e) => setNewRow({ ...newRow, language: e.target.value })} placeholder="Italiano" /></label>
          <label className="text-[10px] uppercase tracking-wide text-slate-500 col-span-1"><span>Greeting</span><input className="mt-1 w-full px-2 py-1 border border-slate-300 rounded text-xs" value={newRow.greeting} onChange={(e) => setNewRow({ ...newRow, greeting: e.target.value })} placeholder="Ciao" /></label>
          <label className="text-[10px] uppercase tracking-wide text-slate-500 col-span-1"><span>Thank you</span><input className="mt-1 w-full px-2 py-1 border border-slate-300 rounded text-xs" value={newRow.thank_you} onChange={(e) => setNewRow({ ...newRow, thank_you: e.target.value })} placeholder="Grazie" /></label>
          <label className="text-[10px] uppercase tracking-wide text-slate-500 col-span-1"><span>Farewell</span><input className="mt-1 w-full px-2 py-1 border border-slate-300 rounded text-xs" value={newRow.farewell} onChange={(e) => setNewRow({ ...newRow, farewell: e.target.value })} placeholder="Arrivederci" /></label>
          <button data-testid="new-phrase-add-btn" onClick={create} className="col-span-1 px-3 py-1.5 bg-[#D35400] hover:bg-[#A04000] text-white rounded text-xs font-semibold">Add</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cultural Celebrations editor
// ---------------------------------------------------------------------------
function CulturalCelebrationsEditor({ api }) {
  const [rows, setRows] = React.useState([]);
  const [cultures, setCultures] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState('');
  const [newRow, setNewRow] = React.useState({ culture_key: '', name: '', notes: '', reminder_days_before: 7, occurrences: [] });
  const [editingOccsId, setEditingOccsId] = React.useState(null);

  const reload = React.useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([api.get('/cultural-celebrations'), api.get('/cultural-phrases')]);
      setRows(Array.isArray(a.data) ? a.data : []);
      setCultures(Array.isArray(b.data) ? b.data : []);
    } catch (e) { setErr(e?.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  }, [api]);
  React.useEffect(() => { reload(); }, [reload]);

  const update = async (id, patch) => {
    try { await api.put(`/cultural-celebrations/${id}`, patch); await reload(); }
    catch (e) { setErr(e?.response?.data?.detail || 'Save failed'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Remove this celebration? Existing client cards will simply stop showing it.')) return;
    try { await api.delete(`/cultural-celebrations/${id}`); await reload(); }
    catch (e) { setErr(e?.response?.data?.detail || 'Delete failed'); }
  };
  const create = async () => {
    if (!newRow.culture_key || !newRow.name.trim()) { setErr('culture + name are required'); return; }
    try {
      await api.post('/cultural-celebrations', newRow);
      setNewRow({ culture_key: '', name: '', notes: '', reminder_days_before: 7, occurrences: [] });
      await reload();
    } catch (e) { setErr(e?.response?.data?.detail || 'Add failed'); }
  };

  return (
    <div data-testid="cultural-celebrations-editor" className="space-y-3 pb-24">
      <div>
        <h2 className="text-base font-bold text-slate-800">Cultural Celebrations</h2>
        <p className="text-xs text-slate-500">Key dates per culture. Drives the Upcoming Celebrations strip on the Cultural Toolkit card + the global dashboard reminder.</p>
      </div>
      {err && <div className="text-xs text-red-700 bg-red-50 ring-1 ring-red-200 rounded px-2 py-1.5">{err}</div>}
      {loading ? <p className="text-xs text-slate-400">Loading…</p> : (
        <div className="space-y-2" data-testid="cultural-celebrations-list">
          {rows.map((r) => (
            <div key={r._id} className="bg-white border border-slate-200 rounded-md p-3 hover:border-amber-300" data-testid={`celebration-row-${r._id}`}>
              <div className="flex items-start gap-2 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">{r.culture_key}{r.is_seed && <span className="ml-1 text-amber-600">(seed)</span>}</div>
                  <Inline value={r.name} onBlur={(v) => update(r._id, { name: v })} className="text-base font-bold" testid={`celebration-${r._id}-name`} />
                </div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 ml-auto flex items-center gap-1">
                  Reminder:
                  <input
                    type="number"
                    value={r.reminder_days_before ?? 7}
                    onChange={(e) => update(r._id, { reminder_days_before: Number(e.target.value || 0) })}
                    className="w-12 px-1 py-0.5 text-xs border border-slate-300 rounded text-right"
                    data-testid={`celebration-${r._id}-reminder-days`}
                  />
                  days
                </div>
                <button onClick={() => remove(r._id)} className="text-rose-500 hover:text-rose-700" data-testid={`celebration-${r._id}-delete`} title="Remove"><Trash2 size={12} /></button>
              </div>
              <div className="mt-2 text-xs text-slate-500">
                <Inline value={r.notes || ''} onBlur={(v) => update(r._id, { notes: v })} placeholder="Notes (optional)" />
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5 items-center">
                <span className="text-[10px] uppercase tracking-wider text-slate-500 mr-1">Occurrences:</span>
                {(r.occurrences || []).map((o, i) => (
                  <span key={i} className="px-2 py-0.5 rounded bg-slate-100 text-[11px] font-mono">{o.year}: {o.date}</span>
                ))}
                <button
                  onClick={() => setEditingOccsId(editingOccsId === r._id ? null : r._id)}
                  className="text-[11px] text-orange-700 hover:underline ml-2"
                  data-testid={`celebration-${r._id}-edit-occs`}
                >
                  {editingOccsId === r._id ? 'Done' : 'Edit dates'}
                </button>
              </div>
              {editingOccsId === r._id && (
                <OccurrencesEditor
                  value={r.occurrences || []}
                  onChange={(occs) => update(r._id, { occurrences: occs })}
                  testIdPrefix={`celebration-${r._id}`}
                />
              )}
            </div>
          ))}
        </div>
      )}

      <div className="border-t border-slate-200 pt-3 mt-3" data-testid="cultural-celebrations-new-row">
        <h3 className="text-xs font-bold text-slate-600 mb-2">Add a celebration</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
          <select data-testid="new-celebration-culture" value={newRow.culture_key} onChange={(e) => setNewRow({ ...newRow, culture_key: e.target.value })} className="px-2 py-1 border border-slate-300 rounded text-xs">
            <option value="">— Culture —</option>
            {cultures.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <input data-testid="new-celebration-name" placeholder="Name (e.g. Lunar Festival)" value={newRow.name} onChange={(e) => setNewRow({ ...newRow, name: e.target.value })} className="px-2 py-1 border border-slate-300 rounded text-xs md:col-span-2" />
          <input type="number" placeholder="Reminder days" value={newRow.reminder_days_before} onChange={(e) => setNewRow({ ...newRow, reminder_days_before: Number(e.target.value || 0) })} className="px-2 py-1 border border-slate-300 rounded text-xs" />
          <button data-testid="new-celebration-add-btn" onClick={create} className="px-3 py-1.5 bg-[#D35400] hover:bg-[#A04000] text-white rounded text-xs font-semibold">Add</button>
        </div>
      </div>
    </div>
  );
}

function OccurrencesEditor({ value, onChange, testIdPrefix }) {
  const [draft, setDraft] = React.useState(value || []);
  React.useEffect(() => setDraft(value || []), [value]);
  const set = (i, patch) => {
    const next = draft.map((d, idx) => idx === i ? { ...d, ...patch } : d);
    setDraft(next);
  };
  const remove = (i) => setDraft(draft.filter((_, idx) => idx !== i));
  const add = () => setDraft([...draft, { year: new Date().getFullYear(), date: '' }]);
  const save = () => { onChange(draft.filter((d) => d.year && d.date)); };
  return (
    <div className="mt-3 bg-slate-50 ring-1 ring-slate-200 rounded p-2 space-y-1.5" data-testid={`${testIdPrefix}-occs-editor`}>
      {draft.map((o, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <input type="number" value={o.year} onChange={(e) => set(i, { year: Number(e.target.value || 0) })} className="w-20 px-2 py-1 border border-slate-300 rounded font-mono" />
          <input type="date" value={o.date} onChange={(e) => set(i, { date: e.target.value })} className="px-2 py-1 border border-slate-300 rounded font-mono" />
          <button onClick={() => remove(i)} className="text-rose-500 hover:text-rose-700"><Trash2 size={11} /></button>
        </div>
      ))}
      <div className="flex gap-2 pt-1">
        <button onClick={add} className="px-2 py-1 text-[11px] text-orange-700 hover:underline" data-testid={`${testIdPrefix}-occs-add`}>+ year</button>
        <button onClick={save} className="ml-auto px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[11px] font-semibold" data-testid={`${testIdPrefix}-occs-save`}>Save dates</button>
      </div>
    </div>
  );
}

// Lightweight inline editable text — debounces save until blur.
function Inline({ value, onBlur, placeholder, className, testid }) {
  const [v, setV] = React.useState(value || '');
  React.useEffect(() => setV(value || ''), [value]);
  return (
    <input
      type="text"
      value={v}
      placeholder={placeholder || ''}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => { if ((v || '') !== (value || '')) onBlur(v); }}
      className={`w-full px-2 py-1 border border-transparent hover:border-slate-200 focus:border-orange-300 focus:outline-none rounded text-xs ${className || ''}`}
      data-testid={testid}
    />
  );
}



// ---------------------------------------------------------------------------
// Defect Catalogue editor — per-roof-subtype defect list with labour +
// materials rates per unit (each / lm / m² / etc). Drives the Repairs
// and Restoration quote builders.
// ---------------------------------------------------------------------------
function DefectCatalogueEditor({ api }) {
  const [rows, setRows] = React.useState([]);
  const [subs, setSubs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState('');
  const [filter, setFilter] = React.useState('');
  const [newRow, setNewRow] = React.useState({ roof_subtype: '', defect_name: '', unit: 'each', repair_requirement: '', labour_rate: 0, materials_rate: 0 });

  const reload = React.useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get('/defect-catalogue'),
        api.get('/roof-subtypes'),
      ]);
      setRows(Array.isArray(a.data) ? a.data : []);
      setSubs(Array.isArray(b.data) ? b.data : []);
    } catch (e) { setErr(e?.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  }, [api]);
  React.useEffect(() => { reload(); }, [reload]);

  const update = async (id, patch) => {
    try { await api.put(`/defect-catalogue/${id}`, patch); await reload(); }
    catch (e) { setErr(e?.response?.data?.detail || 'Save failed'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Remove this defect? Existing quotes already built will still reference it as a snapshot.')) return;
    try { await api.delete(`/defect-catalogue/${id}`); await reload(); }
    catch (e) { setErr(e?.response?.data?.detail || 'Delete failed'); }
  };
  const create = async () => {
    if (!newRow.roof_subtype || !newRow.defect_name.trim()) { setErr('roof_subtype + defect_name required'); return; }
    try {
      await api.post('/defect-catalogue', newRow);
      setNewRow({ roof_subtype: '', defect_name: '', unit: 'each', repair_requirement: '', labour_rate: 0, materials_rate: 0 });
      await reload();
    } catch (e) { setErr(e?.response?.data?.detail || 'Add failed'); }
  };

  const filtered = filter ? rows.filter(r => r.roof_subtype === filter) : rows;

  return (
    <div data-testid="defect-catalogue-editor" className="space-y-3 pb-24">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-bold text-slate-800">Defect Catalogue</h2>
          <p className="text-xs text-slate-500">One row per (roof_subtype, defect). Drives the Repairs + Restoration quote builders.</p>
        </div>
        <select
          value={filter} onChange={(e) => setFilter(e.target.value)}
          className="px-2 py-1.5 border border-slate-300 rounded text-xs bg-white"
          data-testid="defect-filter-subtype"
        >
          <option value="">All subtypes</option>
          {subs.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </div>
      {err && <div className="text-xs text-red-700 bg-red-50 ring-1 ring-red-200 rounded px-2 py-1.5">{err}</div>}
      {loading ? <p className="text-xs text-slate-400">Loading…</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="defect-catalogue-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-2 py-1.5">Subtype</th>
                <th className="text-left px-2 py-1.5">Defect</th>
                <th className="text-left px-2 py-1.5">Unit</th>
                <th className="text-left px-2 py-1.5">Repair requirement</th>
                <th className="text-right px-2 py-1.5">Labour $</th>
                <th className="text-right px-2 py-1.5">Materials $</th>
                <th className="px-2 py-1.5 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r._id} className="border-b border-slate-100 hover:bg-amber-50/30" data-testid={`defect-row-${r._id}`}>
                  <td className="px-2 py-1.5 text-[11px] text-slate-600 font-semibold whitespace-nowrap">{r.roof_subtype}{r.is_seed && <span className="ml-1 text-[10px] text-amber-600">(seed)</span>}</td>
                  <td className="px-2 py-1"><Inline value={r.defect_name} onBlur={(v) => update(r._id, { defect_name: v })} /></td>
                  <td className="px-2 py-1"><Inline value={r.unit || 'each'} onBlur={(v) => update(r._id, { unit: v })} className="font-mono" /></td>
                  <td className="px-2 py-1"><Inline value={r.repair_requirement || ''} onBlur={(v) => update(r._id, { repair_requirement: v })} /></td>
                  <td className="px-2 py-1 text-right"><Inline value={String(r.labour_rate ?? 0)} onBlur={(v) => update(r._id, { labour_rate: Number(v || 0) })} className="text-right font-mono" testid={`defect-${r._id}-labour`} /></td>
                  <td className="px-2 py-1 text-right"><Inline value={String(r.materials_rate ?? 0)} onBlur={(v) => update(r._id, { materials_rate: Number(v || 0) })} className="text-right font-mono" /></td>
                  <td className="px-2 py-1.5 text-right">
                    <button onClick={() => remove(r._id)} className="text-rose-500 hover:text-rose-700" data-testid={`defect-${r._id}-delete`} title="Remove"><Trash2 size={12} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="border-t border-slate-200 pt-3 mt-3" data-testid="defect-catalogue-new-row">
        <h3 className="text-xs font-bold text-slate-600 mb-2">Add a defect</h3>
        <div className="grid grid-cols-2 md:grid-cols-7 gap-2 items-end">
          <select data-testid="new-defect-subtype" value={newRow.roof_subtype} onChange={(e) => setNewRow({ ...newRow, roof_subtype: e.target.value })} className="px-2 py-1 border border-slate-300 rounded text-xs">
            <option value="">— Subtype —</option>
            {subs.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <input data-testid="new-defect-name" placeholder="Defect name" value={newRow.defect_name} onChange={(e) => setNewRow({ ...newRow, defect_name: e.target.value })} className="px-2 py-1 border border-slate-300 rounded text-xs md:col-span-2" />
          <input placeholder="Unit (each/lm/m²)" value={newRow.unit} onChange={(e) => setNewRow({ ...newRow, unit: e.target.value })} className="px-2 py-1 border border-slate-300 rounded text-xs" />
          <input type="number" placeholder="Labour $" value={newRow.labour_rate} onChange={(e) => setNewRow({ ...newRow, labour_rate: Number(e.target.value || 0) })} className="px-2 py-1 border border-slate-300 rounded text-xs text-right font-mono" />
          <input type="number" placeholder="Materials $" value={newRow.materials_rate} onChange={(e) => setNewRow({ ...newRow, materials_rate: Number(e.target.value || 0) })} className="px-2 py-1 border border-slate-300 rounded text-xs text-right font-mono" />
          <button data-testid="new-defect-add-btn" onClick={create} className="px-3 py-1.5 bg-[#D35400] hover:bg-[#A04000] text-white rounded text-xs font-semibold">Add</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Paint Systems editor — per-subtype coat list ($/m² rates). Drives the
// Stage 2 Painting block of the Restoration quote builder.
// ---------------------------------------------------------------------------
function PaintSystemsEditor({ api }) {
  const [rows, setRows] = React.useState([]);
  const [subs, setSubs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState('');
  const [newRow, setNewRow] = React.useState({ roof_subtype: '', system_name: '', notes: '', coats: [] });

  const reload = React.useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get('/paint-systems'),
        api.get('/roof-subtypes'),
      ]);
      setRows(Array.isArray(a.data) ? a.data : []);
      setSubs(Array.isArray(b.data) ? b.data : []);
    } catch (e) { setErr(e?.response?.data?.detail || 'Load failed'); }
    finally { setLoading(false); }
  }, [api]);
  React.useEffect(() => { reload(); }, [reload]);

  const update = async (id, patch) => {
    try { await api.put(`/paint-systems/${id}`, patch); await reload(); }
    catch (e) { setErr(e?.response?.data?.detail || 'Save failed'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Remove this paint system? Restoration quotes already built will still reference the snapshot.')) return;
    try { await api.delete(`/paint-systems/${id}`); await reload(); }
    catch (e) { setErr(e?.response?.data?.detail || 'Delete failed'); }
  };
  const create = async () => {
    if (!newRow.roof_subtype || !newRow.system_name.trim()) { setErr('roof_subtype + system_name required'); return; }
    try {
      await api.post('/paint-systems', newRow);
      setNewRow({ roof_subtype: '', system_name: '', notes: '', coats: [] });
      await reload();
    } catch (e) { setErr(e?.response?.data?.detail || 'Add failed'); }
  };

  return (
    <div data-testid="paint-systems-editor" className="space-y-3 pb-24">
      <div>
        <h2 className="text-base font-bold text-slate-800">Paint Systems</h2>
        <p className="text-xs text-slate-500">Per-subtype paint coat sequence with $/m² rates. Drives the Stage 2 Painting block of the Restoration quote.</p>
      </div>
      {err && <div className="text-xs text-red-700 bg-red-50 ring-1 ring-red-200 rounded px-2 py-1.5">{err}</div>}
      {loading ? <p className="text-xs text-slate-400">Loading…</p> : (
        <div className="space-y-3" data-testid="paint-systems-list">
          {rows.map((r) => (
            <div key={r._id} className="bg-white border border-slate-200 rounded-md p-3 hover:border-amber-300" data-testid={`paint-system-row-${r._id}`}>
              <div className="flex items-start gap-2 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">{r.roof_subtype}{r.is_seed && <span className="ml-1 text-amber-600">(seed)</span>}</div>
                  <Inline value={r.system_name} onBlur={(v) => update(r._id, { system_name: v })} className="text-base font-bold" testid={`paint-${r._id}-name`} />
                </div>
                <span className="text-[10px] text-slate-500 ml-auto">{(r.coats || []).length} coat{(r.coats || []).length === 1 ? '' : 's'}</span>
                <button onClick={() => remove(r._id)} className="text-rose-500 hover:text-rose-700" data-testid={`paint-${r._id}-delete`} title="Remove"><Trash2 size={12} /></button>
              </div>
              <div className="mt-2 text-xs text-slate-500">
                <Inline value={r.notes || ''} onBlur={(v) => update(r._id, { notes: v })} placeholder="Notes (optional)" />
              </div>
              <CoatsEditor coats={r.coats || []} onChange={(coats) => update(r._id, { coats })} idPrefix={`paint-${r._id}`} />
              <div className="text-right text-[11px] mt-1 text-slate-500">
                Total <span className="font-mono font-bold text-slate-700">${(r.coats || []).reduce((acc, c) => acc + Number(c.rate_per_m2 || 0), 0).toFixed(2)}</span> / m²
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="border-t border-slate-200 pt-3 mt-3" data-testid="paint-systems-new-row">
        <h3 className="text-xs font-bold text-slate-600 mb-2">Add a paint system</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
          <select data-testid="new-paint-subtype" value={newRow.roof_subtype} onChange={(e) => setNewRow({ ...newRow, roof_subtype: e.target.value })} className="px-2 py-1 border border-slate-300 rounded text-xs">
            <option value="">— Subtype —</option>
            {subs.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <input data-testid="new-paint-name" placeholder="System name" value={newRow.system_name} onChange={(e) => setNewRow({ ...newRow, system_name: e.target.value })} className="px-2 py-1 border border-slate-300 rounded text-xs md:col-span-2" />
          <button data-testid="new-paint-add-btn" onClick={create} className="px-3 py-1.5 bg-[#D35400] hover:bg-[#A04000] text-white rounded text-xs font-semibold">Add</button>
        </div>
      </div>
    </div>
  );
}

function CoatsEditor({ coats, onChange, idPrefix }) {
  const [draft, setDraft] = React.useState(coats || []);
  React.useEffect(() => setDraft(coats || []), [coats]);
  const setRow = (i, patch) => setDraft(draft.map((d, idx) => idx === i ? { ...d, ...patch } : d));
  const remove = (i) => setDraft(draft.filter((_, idx) => idx !== i));
  const add = () => setDraft([...draft, { label: '', rate_per_m2: 0, optional: false }]);
  const save = () => onChange(draft.filter(d => (d.label || '').trim()));
  return (
    <div className="mt-2 bg-slate-50 ring-1 ring-slate-200 rounded p-2 space-y-1.5" data-testid={`${idPrefix}-coats-editor`}>
      {draft.map((c, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <input value={c.label || ''} onChange={(e) => setRow(i, { label: e.target.value })} placeholder="Coat label" className="flex-1 px-2 py-1 border border-slate-300 rounded" />
          <input type="number" value={c.rate_per_m2 ?? 0} onChange={(e) => setRow(i, { rate_per_m2: Number(e.target.value || 0) })} className="w-20 px-2 py-1 border border-slate-300 rounded text-right font-mono" />
          <span className="text-[10px] text-slate-500">/m²</span>
          <label className="flex items-center gap-1 text-[10px] text-slate-500 cursor-pointer">
            <input type="checkbox" checked={!!c.optional} onChange={(e) => setRow(i, { optional: e.target.checked })} />
            opt
          </label>
          <button onClick={() => remove(i)} className="text-rose-500 hover:text-rose-700"><Trash2 size={11} /></button>
        </div>
      ))}
      <div className="flex gap-2 pt-1">
        <button onClick={add} className="px-2 py-1 text-[11px] text-orange-700 hover:underline" data-testid={`${idPrefix}-coat-add`}>+ coat</button>
        <button onClick={save} className="ml-auto px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[11px] font-semibold" data-testid={`${idPrefix}-coats-save`}>Save coats</button>
      </div>
    </div>
  );
}

